import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MarketplaceService } from "../../../services/marketplace.service";
import { StateManagementService } from "../../../services/state-management.service";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Marketplace } from "../../../models/marketplace.model";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { faGear } from "@fortawesome/pro-solid-svg-icons";
import { CommonModule } from "@angular/common";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { IconDefinition } from "@fortawesome/fontawesome-common-types";
import { debounceTime, forkJoin, merge, Observable, Subject, takeUntil } from "rxjs";
import { Option } from "../../../models/option.model";
import { UsStates } from "../../../utility/us-states";
import { HttpErrorResponse } from "@angular/common/http";
import { RiskAppetite } from "../../../models/risk-appetite.model";
import { BusinessLines } from "../../../utility/business-lines";
import { NaicsService } from "../../../services/naics.service";
import { NaicsCode } from "../../../models/naics-code.model";
import { CanDeactivateComponent } from "../../../guards/unsaved-changes.guard";
import { ToastService } from "../../../services/toast.service";
import { MarketplaceTypeEnum } from "../../../models/marketplace-type.enum";
import { OpportunitySource } from "../../../models/opportunity-source.model";
import { OpportunityService } from "../../../services/opportunity.service";
import { FeatureFlagService } from "../../../services/feature-flag.service";
import { AgencyService } from "../../../services/agency.service";
import { Page } from "../../../models/page.model";
import { Agency } from "../../../models/agency.model";
import { MarketplaceEmailRecipient } from "../../../models/marketplace-email-recipient.model";
import { PaymentOptionEnum } from "../../../models/payment-option.enum";
import { ReferralDeliveryMethodEnum } from "./ReferralDeliveryMethodEnum";
import { EmailTemplateEnum } from "./EmailTemplateEnum";
import { ApiTypeEnum } from "../../../models/api-type.enum";
import { IwUser } from "../../../models/iw-user.model";
import { UserService } from "../../../services/user.service";
import { IwTeamMember } from "../../../models/iw-team-member.model";
import { BankHolidayService } from "../../../services/bank-holiday.service";
import { Holiday } from "../../../models/holiday.model";
import { CustomHoliday } from "../../../models/custom-holiday.model";
import { RiskAppetiteBusinessLine } from "../../../models/risk-appetite-business-line.model";
import { BusinessLineAppetiteEnum } from "../../../models/business-line-appetite.enum";
import { BusinessLineAppetiteComponent } from "../business-line-appetite/business-line-appetite.component";
import { BusinessLineCategoryEnum } from "../../../models/business-line-category.enum";
import { InfoModalButtonComponent } from "../../info-modal/info-modal-button.component";
import { InfoModalData } from "../../info-modal/info-modal.model";

/**
 * PURPOSE: responsible for retrieving and displaying the risk appetite of a single marketplace
 */
@Component({
  selector: "app-marketplace",
  standalone: true,
  imports: [
    FaIconComponent,
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    BusinessLineAppetiteComponent,
    InfoModalButtonComponent
  ],
  templateUrl: "./marketplace.component.html",
  styleUrl: "./marketplace.component.scss"
})
export class MarketplaceComponent
  implements OnInit, AfterViewInit, OnDestroy, CanDeactivateComponent
{
  @ViewChild("commercialBusinessLineAppetiteComponent")
  commercialBusinessLineAppetiteComponent!: BusinessLineAppetiteComponent;
  @ViewChild("personalBusinessLineAppetiteComponent")
  personalBusinessLineAppetiteComponent!: BusinessLineAppetiteComponent;
  @ViewChild("lifeAnnuitiesBusinessLineAppetiteComponent")
  lifeAnnuitiesBusinessLineAppetiteComponent!: BusinessLineAppetiteComponent;

  private readonly PRICE_MODE: string = "priceMode";
  private readonly DELIVERED_PRICE_MODE: string = "deliveredPrice";
  private readonly PERCENT_MODIFIER_MODE: string = "percentModifier";
  private readonly EXCLUDE_NAICS_MODE: string = "exclude";
  private readonly INCLUDE_NAICS_MODE: string = "include";
  private readonly RISK_APPETITE_GROUP: string = "riskAppetite";
  private readonly STATE_GROUP: string = "riskAppetite.states";
  private readonly REFERRAL_SOURCES_GROUP: string = "riskAppetite.referralSources";
  private readonly NAICS_MODE: string = "riskAppetite.naicsMode";
  private readonly INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP: string =
    "riskAppetite.individualEmailRecipientSettings";
  private readonly DEFAULT_DELIVERY_START_TIME: string = "00:00:00";
  private readonly DEFAULT_DELIVERY_END_TIME: string = "23:59:59";
  private readonly DEFAULT_TIMEZONE: string = "America/Chicago";
  private readonly TOAST_DURATION: number = 7000;
  private marketplaceId?: number;
  private onDestroy$: Subject<void> = new Subject<void>();
  protected isNewMarketplaceCreationRequest: boolean = false;
  protected isNewMarketplace: boolean = false;
  protected marketplace?: Marketplace;
  protected readonly faGear: IconDefinition = faGear;
  protected marketplaceForm!: FormGroup;
  protected marketplaceTypeEnum: typeof MarketplaceTypeEnum = MarketplaceTypeEnum;
  protected emailTemplateOptions: EmailTemplateEnum[] = Object.values(EmailTemplateEnum);
  protected apiTypeEnum: typeof ApiTypeEnum = ApiTypeEnum;
  protected marketplaceType: string = this.marketplaceTypeEnum.INSTANT;
  protected paymentOptionEnum: typeof PaymentOptionEnum = PaymentOptionEnum;
  protected paymentOption: string = this.paymentOptionEnum.BILLED;
  protected isLoading: boolean = true;
  protected showAgencyIdSpinner: boolean = false;
  protected showAgencyIdNotFoundError: boolean = false;
  protected isAgencyFound: boolean = false;
  protected agency?: Agency;
  protected agencyUsers: IwTeamMember[] = [];
  protected showMarketplaceNameError: boolean = false;
  protected showDefaultPurchasingAgentSpinner: boolean = false;
  protected showDefaultPurchasingAgentNotFoundError: boolean = false;
  protected isDefaultPurchasingAgentFound: boolean = false;
  protected defaultPurchasingAgent?: IwUser;
  protected showDefaultPurchasingAgentEmailAddressError: boolean = false;
  protected showPhoneNumberError: boolean = false;
  protected showDeliveredPriceError: boolean = false;
  protected showPercentModifierError: boolean = false;
  protected showMinReferralPriceError: boolean = false;
  protected showMaxReferralPriceError: boolean = false;
  protected showReferralPriceError: boolean = false;
  protected showNegativePercentModifierError: boolean = false;
  protected showDeliveryDaysError: boolean = false;
  protected showBusinessLineRequiredError: boolean = false;
  protected emailRecipientsIsInvalid: boolean = false;
  protected emailBccRecipientsIsInvalid: boolean = false;
  protected usStates: Option[] = [];
  protected showStatesError: boolean = false;
  protected commercialBusinessLines: Option[] = [];
  protected personalBusinessLines: Option[] = [];
  protected lifeAndAnnuitiesBusinessLines: Option[] = [];
  protected allRiskAppetiteBusinessLines: RiskAppetiteBusinessLine[] = [];
  protected commercialRiskAppetiteBusinessLines: RiskAppetiteBusinessLine[] = [];
  protected personalRiskAppetiteBusinessLines: RiskAppetiteBusinessLine[] = [];
  protected lifeAnnuitiesRiskAppetiteBusinessLines: RiskAppetiteBusinessLine[] = [];
  protected referralSources: OpportunitySource[] = [];
  protected excludedSources?: string[] = [];
  protected showReferralSourceError: boolean = false;
  protected excludedNaicsCodes?: string[] = [];
  protected allNaicsCodes: string[] = [];
  protected naicsInvalid: boolean = false;
  protected showMinCommercialBusinessLinesError: boolean = false;
  protected showMaxCommercialBusinessLinesError: boolean = false;
  protected showCommercialBusinessLinesError: boolean = false;
  protected showMinYearsInBusinessError: boolean = false;
  protected showMaxYearsInBusinessError: boolean = false;
  protected showYearsInBusinessError: boolean = false;
  protected showMinCommercialEstimatedPremiumError: boolean = false;
  protected showMaxCommercialEstimatedPremiumError: boolean = false;
  protected showCommercialEstimatedPremiumError: boolean = false;
  protected showMinNumberOfEmployeesError: boolean = false;
  protected showMaxNumberOfEmployeesError: boolean = false;
  protected showNumberOfEmployeesError: boolean = false;
  protected showMinPersonalBusinessLinesError: boolean = false;
  protected showMaxPersonalBusinessLinesError: boolean = false;
  protected showPersonalBusinessLinesError: boolean = false;
  protected showMinPersonalEstimatedPremiumError: boolean = false;
  protected showMaxPersonalEstimatedPremiumError: boolean = false;
  protected showPersonalEstimatedPremiumError: boolean = false;
  protected showMinLifeAnnuitiesBusinessLinesError: boolean = false;
  protected showMaxLifeAnnuitiesBusinessLinesError: boolean = false;
  protected showLifeAnnuitiesBusinessLinesError: boolean = false;
  protected showMinDeathBenefitError: boolean = false;
  protected showMaxDeathBenefitError: boolean = false;
  protected showDeathBenefitError: boolean = false;
  protected showReferralsPerHourError: boolean = false;
  protected showReferralsPerDayError: boolean = false;
  protected showReferralsPerWeekError: boolean = false;
  protected showReferralsPerMonthError: boolean = false;
  protected bankHolidays: Holiday[] = [];
  protected suppressedHolidays: string[] = [];
  protected isHolidayModalOpen: boolean = false;
  protected holidayModalSelection: Set<string> = new Set<string>();
  // Custom recurring holidays (AD-16175). `customHolidays` is the committed list; the working
  // copy is edited in the modal and only committed to it on Save (Cancel discards).
  protected customHolidays: CustomHoliday[] = [];
  protected customHolidayModalSelection: CustomHoliday[] = [];
  protected bankSectionOpen: boolean = true;
  protected customSectionOpen: boolean = false;
  protected newCustomHolidayName: string = "";
  protected newCustomHolidayMonth: number = 1;
  protected newCustomHolidayDay: number = 1;
  protected customHolidayError: string = "";
  protected readonly monthNames: string[] = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December"
  ];
  protected readonly monthOptions: number[] = Array.from({ length: 12 }, (_, i): number => i + 1);
  protected showEmailRecipientError: boolean = false;
  protected showDuplicateEmailRecipientError: boolean = false;
  protected showDuplicateEmailBccRecipientError: boolean = false;
  protected isSaving: boolean = false;
  protected referralDeliveryMethod: typeof ReferralDeliveryMethodEnum = ReferralDeliveryMethodEnum;
  protected referralDeliveryMethodOptions: ReferralDeliveryMethodEnum[] = Object.values(
    ReferralDeliveryMethodEnum
  );

  protected readonly marketplaceSetupInfo: InfoModalData = {
    title: "Marketplace Setup",
    subtitle: "The core identity and billing configuration for this marketplace.",
    sections: [
      {
        items: [
          {
            label: "API Type",
            description: "Determines how referrals are claimed by this marketplace.",
            bullets: [
              "Instant — The marketplace claims or declines a referral immediately as it arrives.",
              "Duration — The referral remains available for a set window of time before it expires or is reassigned."
            ]
          },
          {
            label: "Is Generic Marketplace",
            description:
              "Toggle on if this marketplace does not contain a custom backend implementation."
          },
          {
            label: "Agency ID",
            description:
              "If applicable, the ID of the agency this marketplace belongs to. Entering a valid ID will display the agency name and contact details for confirmation."
          },
          {
            label: "Is Premier Partner",
            description: "Marks this marketplace as a Premier Partner marketplace. ",
            bullets: [
              "Only visible after a valid Agency ID has been entered.",
              "Premier Partners have access to the Debit Prepaid Balance payment option."
            ]
          },
          {
            label: "Marketplace Name",
            description:
              "The display name used throughout the platform to identify this marketplace partner. Required."
          },
          {
            label: "Legal Entity Name",
            description:
              "The official registered business name of the marketplace partner. Used for legal and billing purposes. Optional."
          },
          {
            label: "Phone Number",
            description:
              "A contact phone number for this marketplace. Format: 10 digits with no spaces or dashes (e.g. 5551234567). Optional."
          },
          {
            label: "Default Purchasing Agent Email Address",
            description:
              "The email address of the agent who will be the default recipient for purchased referrals within the linked agency. Must match an existing user account. Required when an agency is linked."
          },
          {
            label: "Payment Options",
            description: "Controls how this marketplace is charged for delivered referrals.",
            bullets: [
              "Billed — The marketplace is invoiced for each delivered referral.",
              "Debit Prepaid Balance — Referral cost is deducted from a prepaid account balance. Only available for Premier Partner marketplaces."
            ]
          },
          {
            label: "Referral Price Adjustments: % Modifier",
            description:
              "A percentage applied to the base referral price to calculate what this marketplace pays. For example, entering 80 means the marketplace pays 80% more that the standard base price. Negative values (down to -100%) reduce the price."
          },
          {
            label: "Referral Price Adjustments: Delivered Price",
            description:
              "A flat dollar amount charged for each referral delivered to this marketplace, regardless of the standard base price. Only available for Instant-type marketplaces.",
            bullets: ["Only visible when API Type is set to Instant."]
          },
          {
            label: "Referral Price Range: Min. Price / Max. Price",
            description:
              "The platform will only deliver referrals to this marketplace whose price falls within this range. Leave either field blank to apply no lower or upper limit."
          },
          {
            label: "Referral Sources",
            description:
              'The lead sources this marketplace will accept referrals from. At least one source must be selected. Use "Select All" or "Clear Selection" to manage in bulk.'
          }
        ]
      }
    ]
  };

  protected readonly generalAppetiteInfo: InfoModalData = {
    title: "General Appetite",
    subtitle: "Rules that apply to every referral, regardless of line of business.",
    sections: [
      {
        items: [
          {
            label: "Jornaya ID Required",
            description:
              "When on, only referrals that include a Jornaya consent token will be sent. Jornaya is a third-party consent verification service used to confirm that the consumer agreed to be contacted."
          },
          {
            label: "Business Line Required",
            description:
              "When on, referrals that do not include a specific line of business (e.g., Auto, Home) will not be sent to this marketplace."
          },
          {
            label: "First Name Required",
            description:
              "When on, referrals without a first name will not be sent to this marketplace."
          },
          {
            label: "Last Name Required",
            description:
              "When on, referrals without a last name will not be sent to this marketplace."
          },
          {
            label: "City Required",
            description: "When on, referrals without a city will not be sent to this marketplace."
          },
          {
            label: "State Required",
            description: "When on, referrals without a state will not be sent to this marketplace."
          },
          {
            label: "ZIP Code Required",
            description:
              "When on, referrals without a ZIP code will not be sent to this marketplace."
          },
          {
            label: "Email Address Required",
            description:
              "When on, referrals without an email address will not be sent to this marketplace."
          },
          {
            label: "Phone Number Required",
            description:
              "When on, referrals without a phone number will not be sent to this marketplace."
          },
          {
            label: "One To One Consent Required",
            description:
              "When on, this marketplace requires that the consumer has given explicit one-to-one consent — meaning they agreed specifically to be contacted by this marketplace, not just by agencies in general."
          },
          {
            label: "Captures Own Consumer Consent",
            description:
              "When on, this marketplace collects its own consumer consent independently. The platform will not need to validate consent on its behalf."
          },
          {
            label: "Accepts Warm Transfers",
            description:
              "When on, this marketplace will accept live warm-transfer calls — where the consumer is already on the line when the referral is passed."
          },
          {
            label: "Accepts Ineligible",
            description:
              "When on, this marketplace will accept referrals deemed to be ineligible for reasons such as invalid email, duplicate referral, etc.."
          },
          {
            label: "States",
            description:
              'The U.S. states this marketplace will accept referrals from. At least one state must be selected. Use "Select All" or "Clear Selection" to manage in bulk.'
          }
        ]
      }
    ]
  };

  protected readonly commercialAppetiteInfo: InfoModalData = {
    title: "Commercial Appetite",
    subtitle: "Controls which commercial (business) referrals this marketplace will accept.",
    sections: [
      {
        items: [
          {
            label: "Commercial Appetite Toggle",
            description:
              'The toggle next to the "Commercial Appetite" heading turns commercial lines on or off for this marketplace. When off, no commercial referrals will be accepted in this marketplace regardless of the settings below.'
          },
          {
            label: "NAICS (Exclude / Include)",
            description:
              "NAICS codes are 6-digit industry classification codes used to identify the type of business. You can use this field to either include only certain industries or exclude specific ones.",
            bullets: [
              "Exclude — All businesses are accepted except those whose NAICS code appears in the list.",
              "Include — Only businesses whose NAICS code appears in the list will be accepted.",
              "Enter multiple codes separated by commas (e.g. 123456, 223456)."
            ]
          },
          {
            label: "Accepts Commercial Insured",
            description:
              "When on, this marketplace will accept referrals from businesses that already have commercial insurance coverage."
          },
          {
            label: "Accepts Commercial Uninsured",
            description:
              "When on, this marketplace will accept referrals from businesses that do not currently have commercial insurance coverage."
          },
          {
            label: "Business Address Required",
            description:
              "When on, commercial lines referrals without a business street address will not be sent to this marketplace."
          },
          {
            label: "Business Name Required",
            description:
              "When on, commercial lines referrals without a business name will not be sent to this marketplace."
          },
          {
            label: "Commercial Business Lines (Min / Max)",
            description:
              "The minimum and maximum number of commercial business lines a referral must contain.  Only referrals in this range are accepted in this marketplace. Leave blank if there is no restriction.",
            bullets: [
              "Example: Min 1, Max 3 — only referrals with 1 to 3 business lines will qualify."
            ]
          },
          {
            label: "Commercial Business Lines Table",
            description:
              "A breakdown of individual commercial coverage types (e.g., Commercial Auto, Workers Comp, General Liability). Each line can be set to Optional, Required, or Excluded for this marketplace. Use the Select All row to set all items in a column to that option.",
            bullets: [
              "Optional - the referral is accepted in this marketplace with or without this business line.",
              "Required - the referral must contain this business line to be accepted in the marketplace.",
              "Excluded - the referral will not be accepted in the marketplace if it contains this business line."
            ]
          },
          {
            label: "Years in Business (Min / Max)",
            description:
              "The acceptable range of how long the business has been operating. Only referrals in this range are accepted in this marketplace. Enter 0 for new businesses. Leave blank if there is no restriction.",
            bullets: ["Example: Min 0, Max 5 — accepts new businesses up to 5 years old."]
          },
          {
            label: "Number of Employees (Min / Max)",
            description:
              "The acceptable range of how many employees the business has. Enter 0 for sole proprietors. Only referrals in this range are accepted in this marketplace. Leave blank if there is no restriction.",
            bullets: ["Example: Min 1, Max 50 — accepts businesses with 1 to 50 employees."]
          },
          {
            label: "Estimated Premium (Min / Max)",
            description:
              "The range of estimated annual insurance premium the business is expected to pay. Only referrals in this range are accepted in this marketplace. Leave blank if there is no restriction."
          }
        ]
      }
    ]
  };

  protected readonly personalAppetiteInfo: InfoModalData = {
    title: "Personal Appetite",
    subtitle:
      "Controls which personal lines (individual and family) referrals this marketplace will accept.",
    sections: [
      {
        items: [
          {
            label: "Personal Appetite Toggle",
            description:
              'The toggle next to the "Personal Appetite" heading turns personal lines on or off for this marketplace. When off, no personal referrals will be accepted in this marketplace regardless of the settings below.'
          },
          {
            label: "Accepts Personal Insured",
            description:
              "When on, this marketplace will accept referrals from consumers who already have personal insurance coverage."
          },
          {
            label: "Accepts Personal Uninsured",
            description:
              "When on, this marketplace will accept referrals from consumers who do not currently have personal insurance coverage."
          },
          {
            label: "Personal Business Lines (Min / Max)",
            description:
              "The minimum and maximum number of personal insurance lines the consumer must be shopping for. Only referrals in this range are accepted in this marketplace. Leave blank if there is no restriction.",
            bullets: [
              "Example: Min 1, Max 2 — only consumers shopping for 1 to 2 business lines will qualify."
            ]
          },
          {
            label: "Personal Business Lines Table",
            description:
              "A breakdown of individual personal coverage types (e.g., Auto, Home, Renters). Each line can be set to Required, Accepted, or Excluded for this marketplace. Use the Select All row to set all items in a column to that option.",
            bullets: [
              "Optional - the referral is accepted in this marketplace with or without this business line.",
              "Required - the referral must contain this business line to be accepted in the marketplace.",
              "Excluded - the referral will not be accepted in the marketplace if it contains this business line."
            ]
          },
          {
            label: "Estimated Premium (Min / Max)",
            description:
              "The range of estimated annual insurance premium the consumer is expected to pay. Only referrals in this range are accepted in this marketplace. Leave blank if there is no restriction."
          }
        ]
      }
    ]
  };

  protected readonly lifeAnnuitiesAppetiteInfo: InfoModalData = {
    title: "Life & Annuities Appetite",
    subtitle: "Controls which life insurance and annuity referrals this marketplace will accept.",
    sections: [
      {
        items: [
          {
            label: "Life & Annuities Toggle",
            description:
              'The toggle next to the "Life & Annuities Appetite" heading turns this line of business on or off for this marketplace. When off, no life or annuity referrals will be accepted in this marketplace regardless of the settings below.'
          },
          {
            label: "Accepts Medical Exam Consent",
            description:
              "When on, this marketplace will accept applicants who have agreed to undergo a medical exam as part of the underwriting process."
          },
          {
            label: "Accepts Medical Exam Refusal",
            description:
              "When on, this marketplace will accept applicants who have declined to take a medical exam as part of the underwriting process. Some carriers offer no-exam policies — this setting enables those referrals."
          },
          {
            label: "Accepts Tobacco Use Past Year",
            description:
              "When on, this marketplace will accept applicants who have used tobacco products in the past year."
          },
          {
            label: "Accepts No Tobacco Use Past Year",
            description:
              "When on, this marketplace will accept applicants who have not used tobacco products in the past year."
          },
          {
            label: "Life & Annuities Business Lines (Min / Max)",
            description:
              "The minimum and maximum number of life or annuity products the applicant must be interested in. Only referrals in this range are accepted in this marketplace. Leave blank if there is no restriction.",
            bullets: [
              "Example: Min 1, Max 2 — only consumers shopping for 1 to 2 business lines will qualify."
            ]
          },
          {
            label: "Life & Annuities Business Lines Table",
            description:
              "A breakdown of individual life and annuity product types (e.g., Life Insurance, Long Term Care Insurance, Annuities). Each type can be set to Required, Accepted, or Excluded for this marketplace. Use the Select All row to set all items in a column to that option.",
            bullets: [
              "Optional - the referral is accepted in this marketplace with or without this business line.",
              "Required - the referral must contain this business line to be accepted in the marketplace.",
              "Excluded - the referral will not be accepted in the marketplace if it contains this business line."
            ]
          },
          {
            label: "Death Benefit (Min / Max)",
            description:
              "The acceptable range of death benefit (payout) amount the applicant is seeking. Only referrals in this range are accepted in this marketplace. Leave blank if there is no restriction.",
            bullets: [
              "Example: Min $100,000, Max $500,000 — only applicants seeking a death benefit in this range will qualify."
            ]
          }
        ]
      }
    ]
  };

  protected readonly deliverySettingsInfo: InfoModalData = {
    title: "Delivery Settings",
    subtitle: "Controls when and how referrals are sent to this marketplace.",
    sections: [
      {
        items: [
          {
            label: "Referrals per Hour / Day / Week / Month",
            description:
              "Caps the number of referrals sent to this marketplace within each time period. Leave any field blank to apply no limit for that period.",
            bullets: [
              "Per Hour — no more than this many referrals in any single hour.",
              "Per Day — no more than this many referrals in a calendar day.",
              "Per Week — no more than this many referrals in a rolling 7-day window.",
              "Per Month — no more than this many referrals in a calendar month."
            ]
          },
          {
            label: "Referral Delivery Days (Mon – Sun)",
            description:
              "The days of the week on which this marketplace will receive referrals. Referrals that arrive on unselected days do not get accepted into this marketplace. At least one day must be selected."
          },
          {
            label: "Suppress Delivery on Holidays",
            description:
              'When on, referrals will not be accepted into the marketplace on selected U.S. holidays. Use the "Edit holidays" link to choose which holidays apply.',
            bullets: [
              'Click "Edit holidays" to open the holiday picker and select which holidays referrals should be accepted into this marketplace',
              "The counter next to the toggle shows how many holidays are currently selected."
            ]
          },
          {
            label: "Referral Delivery Times: Start / End Time",
            description:
              "The time window during which referrals will be accepted into the marketplace each day. Referrals received outside this window are not accepted into the marketplace."
          },
          {
            label: "Timezone",
            description:
              "The timezone used to interpret the Start and End times. Options: Alaska, Central (default), Eastern, Hawaii, Mountain, Pacific."
          },
          {
            label: "24 hrs/day",
            description:
              "Check this box to allow referral delivery at any time of day, overriding the Start and End time fields."
          },
          {
            label: "Referral Delivery Method: API Delivery",
            description:
              "When on, referrals are sent to the marketplace via an API call (a direct system-to-system connection). Enable to configure the API details.",
            bullets: [
              "API Delivery Method — Select the integration partner (e.g., Momentum AMP, Tivly, Bold Penguin). If none of the named partners apply, choose Generic to configure a custom webhook.",
              "API Type (Generic only) — Choose Basic for username/password authentication, or X-API-KEY for a key-based header.",
              "URL (Generic only) — The web address where referrals will be posted.",
              "API Key (Generic only) — The secret key sent with each referral to authenticate the request."
            ]
          },
          {
            label: "Referral Delivery Method: Email Delivery",
            description:
              "When on, a referral notification email is sent to one or more recipients each time a referral is delivered to the marketplace.",
            bullets: [
              'Email Template — Choose the format of the email notification. "Default" works for most marketplaces, "VIU Commercial" is used by the HUB marketplaces only',
              "Enable Round Robin Email Delivery — When on, each new referral is sent to the next email recipient in rotation rather than to all email recipients simultaneously.",
              "Referral Email Recipients — Enter the email addresses that should receive referral notifications, separated by commas. At least one is required when email delivery is on.",
              "Individual Recipient Delivery Settings — Once recipients are saved, this toggle allows you to customize delivery days, times, and timezone per recipient. Also controls whether each recipient receives an SMS notification if they have a mobile number on file.",
              "Referral Email Bcc Recipients -  Enter the email addresses that should be Bcc'd on referral notifications, separated by commas."
            ]
          }
        ]
      }
    ]
  };

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private router: Router,
    private formBuilder: FormBuilder,
    private readonly marketplaceService: MarketplaceService,
    private readonly naicsService: NaicsService,
    private stateManagementService: StateManagementService,
    private opportunityService: OpportunityService,
    private toastService: ToastService,
    private agencyService: AgencyService,
    private userService: UserService,
    private bankHolidayService: BankHolidayService,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.retrieveFeatureFlags();
    this.getPreLoadData();
    const marketplaceId: string | null = this.activatedRoute.snapshot.paramMap.get("marketplaceId");
    this.marketplaceId =
      marketplaceId != null && marketplaceId !== "new" ? parseInt(marketplaceId) : undefined;
    this.isNewMarketplaceCreationRequest =
      marketplaceId != null && marketplaceId.toLowerCase() === "new";
  }

  ngAfterViewInit(): void {}

  canDeactivate(): boolean {
    return !this.marketplaceForm.dirty || this.isNewMarketplace;
  }

  private retrieveFeatureFlags(): void {
    // NOTE: retrieve applicable feature flags
    /*
     * EXAMPLE:
     *    this.isPercentModifierFeatureEnabled = this.featureFlagService.isEnabled(
     *       FeatureFlagEnum.MARKETPLACE_PERCENT_MODIFIER
     *    );
     */
  }

  private getPreLoadData(): void {
    forkJoin({
      opportunitySources: this.opportunityService.getOpportunitySources(),
      bankHolidays: this.bankHolidayService.getBankHolidays(new Date().getFullYear())
    })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: ({ opportunitySources, bankHolidays }): void => {
          // Set referral sources
          this.referralSources = opportunitySources.sort(
            (a: OpportunitySource, b: OpportunitySource): number =>
              a.sourceName.localeCompare(b.sourceName)
          );

          // Set bank holidays
          this.bankHolidays = bankHolidays;

          // A new, uncustomized configuration starts with the smart default selections;
          // loading an existing marketplace overwrites this with its stored selections
          this.suppressedHolidays = bankHolidays
            .filter((holiday: Holiday): boolean => !!holiday.suppressedByDefault)
            .map((holiday: Holiday): string => holiday.bankHoliday!);

          this.loadMarketplace(false);
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            "Failed to get pre load data (opportunity sources and/or bank holidays). Please refresh the page and try again.",
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
        }
      });
  }

  /**
   * The recognized U.S. bank holidays that can be individually selected (excludes ad-hoc
   * entries, such as the QA-simulated holiday, which carry no BankHoliday identity).
   */
  protected get selectableHolidays(): Holiday[] {
    return this.bankHolidays.filter((holiday: Holiday): boolean => !!holiday.bankHoliday);
  }

  /**
   * The master "Suppress delivery on holidays" toggle is the inverse of the
   * acceptReferralsOnBankHolidays flag persisted on the risk appetite.
   */
  protected isHolidaySuppressionOn(): boolean {
    return !this.marketplaceForm.get("riskAppetite.acceptReferralsOnBankHolidays")?.value;
  }

  protected toggleHolidaySuppression(event: Event): void {
    const suppressionOn: boolean = (event.target as HTMLInputElement).checked;
    const control = this.marketplaceForm.get("riskAppetite.acceptReferralsOnBankHolidays");
    control?.setValue(!suppressionOn);
    control?.markAsDirty();
  }

  protected openHolidayModal(): void {
    this.holidayModalSelection = new Set<string>(this.suppressedHolidays);
    // Snapshot the custom holidays into a working copy so Cancel discards edits
    this.customHolidayModalSelection = this.customHolidays.map(
      (holiday: CustomHoliday): CustomHoliday => ({ ...holiday })
    );
    this.bankSectionOpen = true;
    this.customSectionOpen = false;
    this.resetCustomHolidayForm();
    this.isHolidayModalOpen = true;
  }

  protected closeHolidayModal(): void {
    this.isHolidayModalOpen = false;
  }

  protected toggleHolidayModalSelection(bankHoliday: string): void {
    if (this.holidayModalSelection.has(bankHoliday)) {
      this.holidayModalSelection.delete(bankHoliday);
    } else {
      this.holidayModalSelection.add(bankHoliday);
    }
  }

  protected selectAllHolidays(): void {
    this.holidayModalSelection = new Set<string>(
      this.selectableHolidays.map((holiday: Holiday): string => holiday.bankHoliday!)
    );
  }

  protected clearAllHolidays(): void {
    this.holidayModalSelection = new Set<string>();
  }

  protected saveHolidayModal(): void {
    this.suppressedHolidays = this.selectableHolidays
      .filter((holiday: Holiday): boolean => this.holidayModalSelection.has(holiday.bankHoliday!))
      .map((holiday: Holiday): string => holiday.bankHoliday!);
    // Commit the custom holidays working copy
    this.customHolidays = this.customHolidayModalSelection.map(
      (holiday: CustomHoliday): CustomHoliday => ({ ...holiday })
    );
    this.marketplaceForm.get("riskAppetite")?.markAsDirty();
    this.isHolidayModalOpen = false;
  }

  // ── Custom holidays (AD-16175) ──────────────────────────────────────────────

  protected toggleBankSection(): void {
    this.bankSectionOpen = !this.bankSectionOpen;
  }

  protected toggleCustomSection(): void {
    this.customSectionOpen = !this.customSectionOpen;
  }

  /**
   * The custom holidays in the modal working copy, sorted by month then day (matches the
   * bank holiday list ordering).
   */
  protected get sortedCustomHolidayModalSelection(): CustomHoliday[] {
    return [...this.customHolidayModalSelection].sort(
      (a: CustomHoliday, b: CustomHoliday): number => a.month - b.month || a.day - b.day
    );
  }

  /** Days available for the currently selected month (Feb allows 29 so Leap Day is selectable). */
  protected maxDayForMonth(month: number): number {
    if (month === 2) {
      return 29;
    }
    return [4, 6, 9, 11].includes(month) ? 30 : 31;
  }

  protected get validDayOptions(): number[] {
    return Array.from(
      { length: this.maxDayForMonth(Number(this.newCustomHolidayMonth)) },
      (_, i): number => i + 1
    );
  }

  /** Clamp the selected day if the newly chosen month has fewer days. */
  protected onNewHolidayMonthChange(): void {
    const max: number = this.maxDayForMonth(Number(this.newCustomHolidayMonth));
    if (Number(this.newCustomHolidayDay) > max) {
      this.newCustomHolidayDay = max;
    }
  }

  protected addCustomHoliday(): void {
    const name: string = this.newCustomHolidayName.trim();
    if (!name) {
      this.customHolidayError = "Give the holiday a name.";
      return;
    }
    this.customHolidayModalSelection.push({
      name,
      month: Number(this.newCustomHolidayMonth),
      day: Number(this.newCustomHolidayDay),
      enabled: true
    });
    this.resetCustomHolidayForm();
  }

  protected removeCustomHoliday(customHoliday: CustomHoliday): void {
    this.customHolidayModalSelection = this.customHolidayModalSelection.filter(
      (holiday: CustomHoliday): boolean => holiday !== customHoliday
    );
  }

  protected toggleCustomHolidayEnabled(customHoliday: CustomHoliday): void {
    customHoliday.enabled = !customHoliday.enabled;
  }

  protected get activeCustomHolidayModalCount(): number {
    return this.customHolidayModalSelection.filter(
      (holiday: CustomHoliday): boolean => holiday.enabled
    ).length;
  }

  /**
   * The Delivery Settings summary line, e.g. "6 bank holidays, 2 custom" — counts the committed
   * suppressed bank holidays and the enabled committed custom holidays.
   */
  protected get holidaySummary(): string {
    const bankCount: number = this.suppressedHolidays.length;
    const customCount: number = this.customHolidays.filter(
      (holiday: CustomHoliday): boolean => holiday.enabled
    ).length;
    return `${bankCount} bank holiday${bankCount === 1 ? "" : "s"}, ${customCount} custom`;
  }

  private resetCustomHolidayForm(): void {
    this.newCustomHolidayName = "";
    this.newCustomHolidayMonth = 1;
    this.newCustomHolidayDay = 1;
    this.customHolidayError = "";
  }

  private loadMarketplace(isAfterSave: boolean): void {
    if (this.marketplaceId && !this.isNewMarketplaceCreationRequest) {
      this.marketplaceService
        .getMarketplace(this.marketplaceId)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (marketplace: Marketplace): void => {
            if (marketplace?.marketplaceName) {
              this.stateManagementService.setPageTitle(
                this.capitalizeFirstLetter(marketplace.marketplaceName) +
                  " Marketplace | Momentum Edge"
              );
              this.stateManagementService.pageHeading.set(
                "Marketplace: " + marketplace.marketplaceName
              );
            }
            this.marketplace = marketplace;
            this.excludedSources = marketplace.riskAppetite?.excludedSources;
            this.excludedNaicsCodes = marketplace.riskAppetite?.excludedNaics;
            this.allRiskAppetiteBusinessLines =
              this.marketplace.riskAppetite?.riskAppetiteBusinessLines || [];
            this.commercialRiskAppetiteBusinessLines = this.allRiskAppetiteBusinessLines.filter(
              (riskAppetiteBusinessLine: RiskAppetiteBusinessLine): boolean =>
                riskAppetiteBusinessLine.businessLineCategory ===
                BusinessLineCategoryEnum.COMMERCIAL
            );
            this.personalRiskAppetiteBusinessLines = this.allRiskAppetiteBusinessLines.filter(
              (riskAppetiteBusinessLine: RiskAppetiteBusinessLine): boolean =>
                riskAppetiteBusinessLine.businessLineCategory === BusinessLineCategoryEnum.PERSONAL
            );
            this.lifeAnnuitiesRiskAppetiteBusinessLines = this.allRiskAppetiteBusinessLines.filter(
              (riskAppetiteBusinessLine: RiskAppetiteBusinessLine): boolean =>
                riskAppetiteBusinessLine.businessLineCategory ===
                BusinessLineCategoryEnum.LIFE_ANNUITIES
            );
          },
          error: (error: HttpErrorResponse): void => {
            this.isLoading = false;
            this.toastService.open(
              `Failed to retrieve marketplace [${this.marketplaceId}]. Please refresh the page and try again.`,
              "Close",
              {
                duration: this.TOAST_DURATION,
                panelClass: ["bg-red-600"]
              }
            );
          },
          complete: (): void => {
            if (this.marketplace?.agencyId) {
              this.getAgencyUsers(isAfterSave);
            } else {
              this.loadData(isAfterSave);
            }
          }
        });
    }

    if (this.isNewMarketplaceCreationRequest) {
      this.stateManagementService.setPageTitle("Create Marketplace | Momentum Edge");
      this.stateManagementService.pageHeading.set("Marketplace: Creation");
      this.initializeForm();
      this.getAllNaicsCodes();
      this.setStateBasedOnFeatureFlags();
      this.disableDirectCallSource();
      this.handleFormValueChanges();
      if (isAfterSave) {
        this.resetErrorStateVariables();
        this.isSaving = false;
        this.marketplaceForm.markAsPristine();
        this.toastService.open(
          `Updates to marketplace [ ${this.marketplace?.marketplaceName} ] has been saved successfully.`,
          "Close",
          {
            duration: this.TOAST_DURATION
          }
        );
      }
      this.isLoading = false;
    }
  }

  private loadData(isAfterSave: boolean): void {
    this.initializeForm();
    this.populateForm(this.marketplace!);
    this.getAllNaicsCodes();
    this.setStateBasedOnFeatureFlags();
    this.disableDirectCallSource();
    this.handleFormValueChanges();
    if (this.marketplace?.agencyId) {
      this.getAgencyInfo(this.marketplace.agencyId);
    }
    if (this.marketplace?.defaultPurchasingAgentEmailAddress) {
      this.getDefaultPurchaser(this.marketplace.defaultPurchasingAgentEmailAddress);
    }
    if (isAfterSave) {
      this.resetErrorStateVariables();
      this.isSaving = false;
      this.marketplaceForm.markAsPristine();
      this.toastService.open(
        `Marketplace [ ${this.marketplace?.marketplaceName} ] has been saved successfully.`,
        "Close",
        {
          duration: this.TOAST_DURATION
        }
      );
    }
    this.isLoading = false;
  }

  private getAgencyUsers(isAfterSave: boolean): void {
    if (!this.marketplace || this.marketplace.agencyId == null) {
      return;
    }

    const agencyId: number = this.marketplace.agencyId;
    this.userService
      .getAllAgencyUsersFromAllPages(agencyId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (iwTeamMembers: IwTeamMember[]): void => {
          this.agencyUsers = iwTeamMembers;
          this.setIndividualEmailRecipientMobilePhoneNumber(this.agencyUsers);
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            `Failed to retrieve users associated with agency ID [${agencyId}]. Please refresh the page and try again.`,
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
          this.showAgencyIdSpinner = false;
        },
        complete: (): void => {
          this.loadData(isAfterSave);
        }
      });
  }

  private setIndividualEmailRecipientMobilePhoneNumber(agencyUsers: IwTeamMember[]): void {
    if (!this.marketplace || !this.marketplace.individualEmailRecipientSettings) {
      return;
    }

    this.marketplace.individualEmailRecipientSettings.forEach(
      (emailRecipient: MarketplaceEmailRecipient): void => {
        const matchingUser: IwTeamMember | undefined = agencyUsers.find(
          (user: IwTeamMember): boolean =>
            user.emailAddress?.toLowerCase() === emailRecipient.emailAddress.toLowerCase() &&
            user.mobilePhoneVerified
        );

        if (matchingUser && matchingUser.mobilePhone) {
          emailRecipient.mobilePhone = matchingUser.mobilePhone;
        }
      }
    );
  }

  private initializeForm(): void {
    this.initializeStates();
    this.initializeBusinessLines();

    this.marketplaceForm = this.formBuilder.group({
      // Marketplace Setup
      marketplaceType: this.marketplaceType,
      referralDeliveryMethod: [
        this.marketplace?.referralDeliveryMethod ?? ReferralDeliveryMethodEnum.GENERIC,
        Validators.required
      ],
      generic: [true],
      agencyId: [null, [Validators.min(0)]],
      premierPartnerMarketplace: [false],
      paymentOption: this.paymentOption,
      marketplaceName: ["", [Validators.required]],
      legalEntityName: [""],
      // http/https only -- the funnels render this through innerHTML.
      legalEntityUrl: ["", [Validators.pattern(/^https?:\/\/.+/)]],
      phoneNumber: ["", [this.phoneNumberValidator()]],
      defaultPurchasingAgentEmailAddress: [""],
      priceMode: [this.PERCENT_MODIFIER_MODE],
      deliveredPrice: [null, [Validators.min(0)]],
      percentModifier: [
        0,
        [Validators.required, Validators.pattern(/^-?\d+$/), Validators.min(-100)]
      ],
      riskAppetite: this.formBuilder.group(
        {
          // Marketplace Setup
          referralSources: this.buildReferralSourceControls(),

          // Delivery Settings
          maxReferralsPerHour: [null, [Validators.min(0)]],
          maxReferralsPerDay: [null, [Validators.min(0)]],
          maxReferralsPerWeek: [null, [Validators.min(0)]],
          maxReferralsPerMonth: [null, [Validators.min(0)]],
          acceptReferralsOnMonday: [true],
          acceptReferralsOnTuesday: [true],
          acceptReferralsOnWednesday: [true],
          acceptReferralsOnThursday: [true],
          acceptReferralsOnFriday: [true],
          acceptReferralsOnSaturday: [true],
          acceptReferralsOnSunday: [true],
          acceptReferralsOnBankHolidays: [false],
          referralDeliveryStartTime: [{ value: this.DEFAULT_DELIVERY_START_TIME, disabled: true }],
          referralDeliveryEndTime: [{ value: this.DEFAULT_DELIVERY_END_TIME, disabled: true }],
          referralDeliveryTimezone: [this.DEFAULT_TIMEZONE],
          accept24hrReferralDelivery: [true],
          individualEmailRecipientSettings: this.buildIndividualEmailRecipientSettingControls(),

          // General Appetite
          journayaIdRequired: [false],
          businessLineRequired: [false],
          firstNameRequired: [true],
          lastNameRequired: [true],
          cityRequired: [true],
          stateRequired: [true],
          postalCodeRequired: [true],
          businessAddressRequired: [false],
          businessNameRequired: [false],
          emailAddressRequired: [true],
          phoneNumberRequired: [true],
          oneToOneConsentRequired: [false],
          capturesOwnConsumerConsent: [false],
          acceptIneligible: [false],
          acceptWarmTransfers: [false],
          referralPriceMin: [null, [Validators.min(0)]],
          referralPriceMax: [null, [Validators.min(0)]],
          states: this.buildStateControls(),

          // Commercial Appetite
          acceptCommercialLines: [false],
          naicsMode: [this.EXCLUDE_NAICS_MODE],
          naicsCodes: [""],
          acceptCommercialInsured: [true],
          acceptCommercialUninsured: [true],
          commercialBusinessLinesMin: [null, [Validators.min(0)]],
          commercialBusinessLinesMax: [null, [Validators.min(0)]],
          commercialBusinessLines: this.buildCommercialBusinessLineControls(),
          yearsInBusinessMin: [null, [Validators.min(0), Validators.max(99)]],
          yearsInBusinessMax: [null, [Validators.min(0), Validators.max(99)]],
          numberOfEmployeesMin: [null, [Validators.min(0), Validators.max(999)]],
          numberOfEmployeesMax: [null, [Validators.min(0), Validators.max(999)]],
          commercialEstimatedPremiumMin: [null, [Validators.min(0)]],
          commercialEstimatedPremiumMax: [null, [Validators.min(0)]],

          // Personal Appetite
          acceptPersonalLines: [false],
          acceptPersonalInsured: [true],
          acceptPersonalUninsured: [true],
          personalBusinessLinesMin: [null, [Validators.min(0)]],
          personalBusinessLinesMax: [null, [Validators.min(0)]],
          personalBusinessLines: this.buildPersonalBusinessLineControls(),
          personalEstimatedPremiumMin: [null, [Validators.min(0)]],
          personalEstimatedPremiumMax: [null, [Validators.min(0)]],

          // Life & Annuities Appetite
          acceptLifeAnnuitiesLines: [false],
          acceptMedicalExamConsent: [true],
          acceptMedicalExamRefusal: [true],
          acceptTobaccoUsePastYear: [true],
          acceptNoTobaccoUsePastYear: [true],
          lifeAnnuitiesBusinessLinesMin: [null, [Validators.min(0)]],
          lifeAnnuitiesBusinessLinesMax: [null, [Validators.min(0)]],
          lifeAndAnnuitiesBusinessLines: this.buildLifeAndAnnuitiesBusinessLineControls(),
          deathBenefitMin: [null, [Validators.min(0)]],
          deathBenefitMax: [null, [Validators.min(0)]]
        },
        { validators: this.hasAtLeastOneDeliveryDay() }
      ),

      // Delivery Settings
      deliverToApi: [true],
      deliverToEmailAddress: [false],
      enableRoundRobinDelivery: [false],
      deliveryApiUrl: [""],
      deliveryApiKey: [""],
      deliveryApiType: ["BASIC"],
      enableIndividualEmailDeliverySettings: [false],
      emailTemplate: [
        this.marketplace?.emailTemplate ?? EmailTemplateEnum.DEFAULT,
        Validators.required
      ],
      emailRecipients: [""],
      emailBccRecipients: [""]
    });
  }

  private resetErrorStateVariables(): void {
    this.showAgencyIdNotFoundError = false;
    this.showMarketplaceNameError = false;
    this.showPhoneNumberError = false;
    this.showDefaultPurchasingAgentNotFoundError = false;
    this.showDefaultPurchasingAgentEmailAddressError = false;
    this.showDeliveredPriceError = false;
    this.showPercentModifierError = false;
    this.showMinReferralPriceError = false;
    this.showMaxReferralPriceError = false;
    this.showReferralPriceError = false;
    this.showNegativePercentModifierError = false;
    this.showDeliveryDaysError = false;
    this.showBusinessLineRequiredError = false;
    this.emailRecipientsIsInvalid = false;
    this.emailBccRecipientsIsInvalid = false;
    this.showStatesError = false;
    this.showReferralSourceError = false;
    this.showReferralSourceError = false;
    this.showMinCommercialBusinessLinesError = false;
    this.showMaxCommercialBusinessLinesError = false;
    this.showCommercialBusinessLinesError = false;
    this.showMinYearsInBusinessError = false;
    this.showMaxYearsInBusinessError = false;
    this.showYearsInBusinessError = false;
    this.showMinCommercialEstimatedPremiumError = false;
    this.showMaxCommercialEstimatedPremiumError = false;
    this.showCommercialEstimatedPremiumError = false;
    this.showMinNumberOfEmployeesError = false;
    this.showMaxNumberOfEmployeesError = false;
    this.showNumberOfEmployeesError = false;
    this.showMinPersonalBusinessLinesError = false;
    this.showMaxPersonalBusinessLinesError = false;
    this.showPersonalBusinessLinesError = false;
    this.showMinPersonalEstimatedPremiumError = false;
    this.showMaxPersonalEstimatedPremiumError = false;
    this.showPersonalEstimatedPremiumError = false;
    this.showMinLifeAnnuitiesBusinessLinesError = false;
    this.showMaxLifeAnnuitiesBusinessLinesError = false;
    this.showLifeAnnuitiesBusinessLinesError = false;
    this.showMinDeathBenefitError = false;
    this.showMaxDeathBenefitError = false;
    this.showDeathBenefitError = false;
    this.showReferralsPerHourError = false;
    this.showReferralsPerHourError = false;
    this.showReferralsPerDayError = false;
    this.showReferralsPerWeekError = false;
    this.showReferralsPerMonthError = false;
    this.showEmailRecipientError = false;
    this.showDuplicateEmailRecipientError = false;
    this.showDuplicateEmailBccRecipientError = false;
  }

  /**
   * Retrieves the list of states and sorts them alphabetically in ASC order
   */
  private initializeStates(): void {
    this.usStates = UsStates.states.sort((a: Option, b: Option): number =>
      a.displayName.localeCompare(b.displayName)
    );
  }

  /**
   * Retrieves the list of business lines and sorts them alphabetically in ASC order
   */
  private initializeBusinessLines(): void {
    this.commercialBusinessLines = BusinessLines.commercialBusinessLines.sort(
      (a: Option, b: Option): number => a.displayName.localeCompare(b.displayName)
    );

    this.personalBusinessLines = BusinessLines.personalBusinessLines.sort(
      (a: Option, b: Option): number => a.displayName.localeCompare(b.displayName)
    );

    this.lifeAndAnnuitiesBusinessLines = BusinessLines.lifeAndAnnuitiesBusinessLines.sort(
      (a: Option, b: Option): number => a.displayName.localeCompare(b.displayName)
    );
  }

  protected onCommercialBusinessLineAppetiteChanged(): void {
    // Register change to the form
    this.marketplaceForm.markAsDirty();

    // Capture the update Business Lines
    const businessLineStatusMap: Map<string, BusinessLineAppetiteEnum> =
      this.commercialBusinessLineAppetiteComponent.getAllBusinessLinesAppetites();
    this.commercialRiskAppetiteBusinessLines = [];
    const riskAppetiteId: number | undefined = this.marketplace?.riskAppetite?.riskAppetiteId;
    businessLineStatusMap.forEach(
      (businessLineAppetite: BusinessLineAppetiteEnum, businessLineCode: string): void => {
        if (businessLineAppetite !== BusinessLineAppetiteEnum.OPTIONAL) {
          this.commercialRiskAppetiteBusinessLines.push({
            riskAppetiteId: riskAppetiteId,
            businessLineCode: businessLineCode,
            businessLineCategory: BusinessLineCategoryEnum.COMMERCIAL,
            excluded: businessLineAppetite === BusinessLineAppetiteEnum.EXCLUDED
          });
        }
      }
    );
  }

  protected onPersonalBusinessLineAppetiteChanged(): void {
    // Register change to the form
    this.marketplaceForm.markAsDirty();

    // Capture the update Business Lines
    const businessLineStatusMap: Map<string, BusinessLineAppetiteEnum> =
      this.personalBusinessLineAppetiteComponent.getAllBusinessLinesAppetites();
    this.personalRiskAppetiteBusinessLines = [];
    const riskAppetiteId: number | undefined = this.marketplace?.riskAppetite?.riskAppetiteId;
    businessLineStatusMap.forEach(
      (businessLineAppetite: BusinessLineAppetiteEnum, businessLineCode: string): void => {
        if (businessLineAppetite !== BusinessLineAppetiteEnum.OPTIONAL) {
          this.personalRiskAppetiteBusinessLines.push({
            riskAppetiteId: riskAppetiteId,
            businessLineCode: businessLineCode,
            businessLineCategory: BusinessLineCategoryEnum.PERSONAL,
            excluded: businessLineAppetite === BusinessLineAppetiteEnum.EXCLUDED
          });
        }
      }
    );
  }

  protected onLifeAnnuitiesBusinessLineAppetiteChanged(): void {
    // Register change to the form
    this.marketplaceForm.markAsDirty();

    // Capture the update Business Lines
    const businessLineStatusMap: Map<string, BusinessLineAppetiteEnum> =
      this.lifeAnnuitiesBusinessLineAppetiteComponent.getAllBusinessLinesAppetites();
    this.lifeAnnuitiesRiskAppetiteBusinessLines = [];
    const riskAppetiteId: number | undefined = this.marketplace?.riskAppetite?.riskAppetiteId;
    businessLineStatusMap.forEach(
      (businessLineAppetite: BusinessLineAppetiteEnum, businessLineCode: string): void => {
        if (businessLineAppetite !== BusinessLineAppetiteEnum.OPTIONAL) {
          this.lifeAnnuitiesRiskAppetiteBusinessLines.push({
            riskAppetiteId: riskAppetiteId,
            businessLineCode: businessLineCode,
            businessLineCategory: BusinessLineCategoryEnum.LIFE_ANNUITIES,
            excluded: businessLineAppetite === BusinessLineAppetiteEnum.EXCLUDED
          });
        }
      }
    );
  }

  private buildStateControls(): FormGroup {
    const stateGroup: FormGroup<{}> = this.formBuilder.group({});
    this.usStates.forEach((state: Option): void => {
      if (state.field) {
        // Default to all states to unselected
        stateGroup.addControl(state.field, this.formBuilder.control(false));
      }
    });
    return stateGroup;
  }

  private phoneNumberValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const digitsOnly: string = control.value.replaceAll(/\D/g, "");
      if (digitsOnly.length !== 10) {
        this.showPhoneNumberError = true;
        return { invalidPhoneNumber: true };
      }

      this.showPhoneNumberError = false;
      return null;
    };
  }

  private buildReferralSourceControls(): FormGroup {
    const referralSourcesGroup: FormGroup<{}> = this.formBuilder.group({});
    this.referralSources.forEach((source: OpportunitySource): void => {
      if (this.isNewMarketplaceCreationRequest) {
        referralSourcesGroup.addControl(source.sourceName, this.formBuilder.control(false));
      } else {
        if (this.excludedSources) {
          referralSourcesGroup.addControl(
            source.sourceName,
            this.formBuilder.control(!this.excludedSources.includes(source.sourceName))
          );
        } else {
          referralSourcesGroup.addControl(source.sourceName, this.formBuilder.control(false));
        }
      }
    });
    return referralSourcesGroup;
  }

  private buildIndividualEmailRecipientSettingControls(): FormGroup {
    const individualEmailRecipientSettingGroup: FormGroup<{}> = this.formBuilder.group({});
    if (this.marketplace && this.marketplace.individualEmailRecipientSettings) {
      this.marketplace.individualEmailRecipientSettings.forEach(
        (recipient: MarketplaceEmailRecipient): void => {
          const recipientId: number = recipient.id;
          individualEmailRecipientSettingGroup.addControl(
            "emailAddress-" + recipientId,
            this.formBuilder.control("")
          );
          individualEmailRecipientSettingGroup.addControl(
            "enableSmsNotification-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "notificationSequenceId-" + recipientId,
            this.formBuilder.control(null)
          );
          individualEmailRecipientSettingGroup.addControl(
            "sameDeliveryDaysAsMarketplace-" + recipientId,
            this.formBuilder.control(false)
          );
          individualEmailRecipientSettingGroup.addControl(
            "acceptReferralsOnMonday-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "acceptReferralsOnTuesday-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "acceptReferralsOnWednesday-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "acceptReferralsOnThursday-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "acceptReferralsOnFriday-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "acceptReferralsOnSaturday-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "acceptReferralsOnSunday-" + recipientId,
            this.formBuilder.control(true)
          );
          individualEmailRecipientSettingGroup.addControl(
            "sameDeliveryTimesAsMarketplace-" + recipientId,
            this.formBuilder.control(false)
          );
          individualEmailRecipientSettingGroup.addControl(
            "referralDeliveryStartTime-" + recipientId,
            this.formBuilder.control(this.DEFAULT_DELIVERY_START_TIME)
          );
          individualEmailRecipientSettingGroup.addControl(
            "referralDeliveryEndTime-" + recipientId,
            this.formBuilder.control(this.DEFAULT_DELIVERY_END_TIME)
          );
          individualEmailRecipientSettingGroup.addControl(
            "referralDeliveryTimezone-" + recipientId,
            this.formBuilder.control(this.DEFAULT_TIMEZONE)
          );
          individualEmailRecipientSettingGroup.addControl(
            "accept24hrReferralDelivery-" + recipientId,
            this.formBuilder.control(true)
          );
        }
      );
    }

    // Add the custom validator
    individualEmailRecipientSettingGroup.setValidators(
      this.atLeastOneDaySelectedForIndividualEmailRecipientValidator()
    );

    return individualEmailRecipientSettingGroup;
  }

  private atLeastOneDaySelectedForIndividualEmailRecipientValidator(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      if (!formGroup || !this.marketplace || !this.marketplace.individualEmailRecipientSettings) {
        return null;
      }

      const errors: ValidationErrors = {};

      this.marketplace.individualEmailRecipientSettings.forEach(
        (recipient: MarketplaceEmailRecipient): void => {
          const days: string[] = [
            "acceptReferralsOnMonday",
            "acceptReferralsOnTuesday",
            "acceptReferralsOnWednesday",
            "acceptReferralsOnThursday",
            "acceptReferralsOnFriday",
            "acceptReferralsOnSaturday",
            "acceptReferralsOnSunday"
          ];

          const hasAtLeastOneDay: boolean = days.some((day: string): boolean | null => {
            const control: AbstractControl<any, any> | null = formGroup.get(
              `${day}-${recipient.id}`
            );
            return control && control.value === true;
          });

          if (!hasAtLeastOneDay) {
            errors[`noDeliveryDaySelected-${recipient.id}`] = {
              message: `At least one day must be selected for recipient ${recipient.id}`
            };
          }
        }
      );

      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  private buildCommercialBusinessLineControls(): FormGroup {
    const businessLineGroup: FormGroup<{}> = this.formBuilder.group({});
    this.commercialBusinessLines.forEach((businessLine: Option): void => {
      if (businessLine.field) {
        businessLineGroup.addControl(
          businessLine.field,
          this.formBuilder.control(BusinessLineAppetiteEnum.EXCLUDED)
        );
      }
    });
    return businessLineGroup;
  }

  private buildPersonalBusinessLineControls(): FormGroup {
    const businessLineGroup: FormGroup<{}> = this.formBuilder.group({});
    this.personalBusinessLines.forEach((businessLine: Option): void => {
      if (businessLine.field) {
        businessLineGroup.addControl(
          businessLine.field,
          this.formBuilder.control(BusinessLineAppetiteEnum.EXCLUDED)
        );
      }
    });
    return businessLineGroup;
  }

  private buildLifeAndAnnuitiesBusinessLineControls(): FormGroup {
    const businessLineGroup: FormGroup<{}> = this.formBuilder.group({});
    this.lifeAndAnnuitiesBusinessLines.forEach((businessLine: Option): void => {
      if (businessLine.field) {
        businessLineGroup.addControl(
          businessLine.field,
          this.formBuilder.control(BusinessLineAppetiteEnum.EXCLUDED)
        );
      }
    });
    return businessLineGroup;
  }

  private capitalizeFirstLetter(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
  }

  private hasAtLeastOneDeliveryDay(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
      const monday: boolean = formGroup.get("acceptReferralsOnMonday")?.value;
      const tuesday: boolean = formGroup.get("acceptReferralsOnTuesday")?.value;
      const wednesday: boolean = formGroup.get("acceptReferralsOnWednesday")?.value;
      const thursday: boolean = formGroup.get("acceptReferralsOnThursday")?.value;
      const friday: boolean = formGroup.get("acceptReferralsOnFriday")?.value;
      const saturday: boolean = formGroup.get("acceptReferralsOnSaturday")?.value;
      const sunday: boolean = formGroup.get("acceptReferralsOnSunday")?.value;

      const isValid: boolean =
        monday || tuesday || wednesday || thursday || friday || saturday || sunday;

      return isValid ? null : { noDeliveryDaySelected: true };
    };
  }

  private getAllNaicsCodes(): void {
    this.naicsService
      .getSixDigitNaicsCodes()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (codes: NaicsCode[]): void => {
          this.allNaicsCodes = codes.map((code: NaicsCode): string => code.code!);
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            `Error encountered while retrieving NAICs codes. Please refresh the page and try again.`,
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
        }
      });
  }

  private populateForm(marketplace: Marketplace): void {
    if (marketplace) {
      if (this.marketplaceTypeEnum.INSTANT == marketplace.type) {
        this.marketplaceForm.patchValue({
          deliveredPrice: marketplace.deliveredPrice
        });
      }
      this.marketplaceForm.patchValue({
        marketplaceType: marketplace.type,
        generic: marketplace.generic,
        referralDeliveryMethod: marketplace.referralDeliveryMethod,
        agencyId: marketplace.agencyId,
        defaultPurchasingAgentEmailAddress: marketplace.defaultPurchasingAgentEmailAddress,
        premierPartnerMarketplace: marketplace.premierPartnerMarketplace,
        paymentOption: marketplace.debitPrepaidBalance
          ? this.paymentOptionEnum.DEBIT_PREPAID_BALANCE
          : marketplace.billed
            ? this.paymentOptionEnum.BILLED
            : null,
        marketplaceName: marketplace.marketplaceName,
        legalEntityName: marketplace.legalEntityName,
        legalEntityUrl: marketplace.legalEntityUrl,
        phoneNumber: marketplace.phoneNumber?.replace("+1", ""),
        priceMode: marketplace.applyDeliveredPrice
          ? this.DELIVERED_PRICE_MODE
          : this.PERCENT_MODIFIER_MODE,
        percentModifier: this.convertPriceModifierToPercentModifier(marketplace.priceModifier),
        deliverToApi: marketplace.deliverToApi,
        deliveryApiUrl: marketplace.deliveryApiUrl,
        deliveryApiKey: marketplace.deliveryApiKey,
        deliveryApiType: marketplace.deliveryApiType,
        emailTemplate: marketplace.emailTemplate ?? EmailTemplateEnum.DEFAULT,
        deliverToEmailAddress: marketplace.deliverToEmailAddress,
        enableRoundRobinDelivery: marketplace.enableRoundRobinDelivery,
        emailRecipients: marketplace.emailRecipients?.join(", "),
        enableIndividualEmailDeliverySettings: marketplace.enableIndividualEmailDeliverySettings,
        emailBccRecipients: marketplace.emailBccRecipients?.join(", ")
      });

      if (marketplace.riskAppetite) {
        const riskAppetite: RiskAppetite = marketplace.riskAppetite;
        const riskAppetiteFormGroup = this.marketplaceForm.get(
          this.RISK_APPETITE_GROUP
        ) as FormGroup;

        riskAppetiteFormGroup?.patchValue({
          journayaIdRequired: riskAppetite.journayaIdRequired,
          businessLineRequired: riskAppetite.businessLineRequired,
          firstNameRequired: riskAppetite.firstNameRequired,
          lastNameRequired: riskAppetite.lastNameRequired,
          cityRequired: riskAppetite.cityRequired,
          stateRequired: riskAppetite.stateRequired,
          postalCodeRequired: riskAppetite.postalCodeRequired,
          businessAddressRequired: riskAppetite.businessAddressRequired,
          businessNameRequired: riskAppetite.businessNameRequired,
          emailAddressRequired: riskAppetite.emailAddressRequired,
          phoneNumberRequired: riskAppetite.phoneNumberRequired,
          oneToOneConsentRequired: marketplace.oneToOneConsentRequired,
          capturesOwnConsumerConsent: marketplace.capturesOwnConsumerConsent,
          acceptIneligible: marketplace.acceptIneligible,
          acceptCommercialLines: riskAppetite.acceptCommercialLines,
          acceptCommercialInsured: riskAppetite.acceptCommercialInsured,
          acceptCommercialUninsured: riskAppetite.acceptCommercialUninsured,
          commercialBusinessLinesMin: riskAppetite.commercialBusinessLinesMin,
          commercialBusinessLinesMax: riskAppetite.commercialBusinessLinesMax,
          yearsInBusinessMin: riskAppetite.yearsInBusinessMin,
          yearsInBusinessMax: riskAppetite.yearsInBusinessMax,
          numberOfEmployeesMin: riskAppetite.numberOfEmployeesMin,
          numberOfEmployeesMax: riskAppetite.numberOfEmployeesMax,
          commercialEstimatedPremiumMin: riskAppetite.commercialEstimatedPremiumMin,
          commercialEstimatedPremiumMax: riskAppetite.commercialEstimatedPremiumMax,
          acceptPersonalLines: riskAppetite.acceptPersonalLines,
          acceptPersonalInsured: riskAppetite.acceptPersonalInsured,
          acceptPersonalUninsured: riskAppetite.acceptPersonalUninsured,
          personalBusinessLinesMin: riskAppetite.personalBusinessLinesMin,
          personalBusinessLinesMax: riskAppetite.personalBusinessLinesMax,
          personalEstimatedPremiumMin: riskAppetite.personalEstimatedPremiumMin,
          personalEstimatedPremiumMax: riskAppetite.personalEstimatedPremiumMax,
          acceptLifeAnnuitiesLines: riskAppetite.acceptLifeAnnuitiesLines,
          acceptMedicalExamConsent: riskAppetite.acceptMedicalExamConsent,
          acceptMedicalExamRefusal: riskAppetite.acceptMedicalExamRefusal,
          acceptTobaccoUsePastYear: riskAppetite.acceptTobaccoUsePastYear,
          acceptNoTobaccoUsePastYear: riskAppetite.acceptNoTobaccoUsePastYear,
          lifeAnnuitiesBusinessLinesMin: riskAppetite.lifeAnnuitiesBusinessLinesMin,
          lifeAnnuitiesBusinessLinesMax: riskAppetite.lifeAnnuitiesBusinessLinesMax,
          deathBenefitMin: riskAppetite.deathBenefitMin,
          deathBenefitMax: riskAppetite.deathBenefitMax,
          maxReferralsPerHour: riskAppetite.maxReferralsPerHour,
          maxReferralsPerDay: riskAppetite.maxReferralsPerDay,
          maxReferralsPerWeek: riskAppetite.maxReferralsPerWeek,
          maxReferralsPerMonth: riskAppetite.maxReferralsPerMonth,
          acceptReferralsOnMonday: riskAppetite.acceptReferralsOnMonday,
          acceptReferralsOnTuesday: riskAppetite.acceptReferralsOnTuesday,
          acceptReferralsOnWednesday: riskAppetite.acceptReferralsOnWednesday,
          acceptReferralsOnThursday: riskAppetite.acceptReferralsOnThursday,
          acceptReferralsOnFriday: riskAppetite.acceptReferralsOnFriday,
          acceptReferralsOnSaturday: riskAppetite.acceptReferralsOnSaturday,
          acceptReferralsOnSunday: riskAppetite.acceptReferralsOnSunday,
          acceptReferralsOnBankHolidays: riskAppetite.acceptReferralsOnBankHolidays,
          referralDeliveryStartTime: riskAppetite.referralDeliveryStartTime,
          referralDeliveryEndTime: riskAppetite.referralDeliveryEndTime,
          accept24hrReferralDelivery: riskAppetite.accept24hrReferralDelivery,
          referralDeliveryTimezone: riskAppetite.referralDeliveryTimezone,
          acceptWarmTransfers: riskAppetite.acceptWarmTransfers,
          referralPriceMin: riskAppetite.referralPriceMin,
          referralPriceMax: riskAppetite.referralPriceMax
        });

        // Set the individually selected holidays on which delivery is suppressed
        this.suppressedHolidays = riskAppetite.suppressedHolidays ?? [];

        // Set the admin-managed custom recurring holidays (AD-16175)
        this.customHolidays = riskAppetite.customHolidays ?? [];

        // Set the state of the agency
        if (marketplace.agencyId) {
          this.isAgencyFound = true;
        }

        // Set the payment options state
        if (marketplace.debitPrepaidBalance) {
          this.paymentOption = this.paymentOptionEnum.DEBIT_PREPAID_BALANCE;
        }

        if (marketplace.billed) {
          this.paymentOption = this.paymentOptionEnum.BILLED;
        }

        // Set the state of the start and end referral delivery times
        if (riskAppetite.accept24hrReferralDelivery) {
          this.marketplaceForm.get("riskAppetite.referralDeliveryStartTime")?.disable();
          this.marketplaceForm.get("riskAppetite.referralDeliveryEndTime")?.disable();
        } else {
          this.marketplaceForm.get("riskAppetite.referralDeliveryStartTime")?.enable();
          this.marketplaceForm.get("riskAppetite.referralDeliveryEndTime")?.enable();
        }

        // Initialize NAICS codes
        const excludedNaics: string[] | undefined = riskAppetite.excludedNaics;
        if (excludedNaics && excludedNaics.length > 0) {
          riskAppetiteFormGroup?.patchValue({ naicsMode: this.EXCLUDE_NAICS_MODE });
          riskAppetiteFormGroup?.patchValue({ naicsCodes: excludedNaics.join(", ") });
        }

        // Initialize selected states
        const stateCodes: string[] | undefined = riskAppetite.stateCodes;
        if (stateCodes && stateCodes.length > 0) {
          const statesGroup = this.marketplaceForm.get(this.STATE_GROUP) as FormGroup;
          // Clear the defaulted states as all states defaulted to true
          Object.keys(statesGroup.controls).forEach((stateCode: string): void => {
            statesGroup.get(stateCode)?.setValue(false);
          });
          // Set the applicable states
          stateCodes.forEach((stateCode: string): void => {
            if (statesGroup.contains(stateCode)) {
              statesGroup.get(stateCode)?.setValue(true);
            }
          });
        }

        // Initialize selected referral sources
        const excludedReferralSources: string[] | undefined = riskAppetite.excludedSources;
        if (excludedReferralSources && excludedReferralSources.length > 0) {
          const referralSourceGroup = this.marketplaceForm.get(
            this.REFERRAL_SOURCES_GROUP
          ) as FormGroup;
          excludedReferralSources.forEach((referralSource: string): void => {
            if (referralSourceGroup.contains(referralSource)) {
              // Note get([]) with square brackets used as form controls with periods like commercialinsurance.net sometimes has issues in reactive forms
              referralSourceGroup.get([referralSource])?.setValue(false);
            }
          });
        }

        this.initializeIndividualEmailRecipientSettings(marketplace);
      }
    } else {
      // Set the state of the start and end referral delivery times
      // riskAppetite.accept24hrReferralDelivery is true by default, so the input fields should be disabled
      this.marketplaceForm.get("riskAppetite.referralDeliveryStartTime")?.disable();
      this.marketplaceForm.get("riskAppetite.referralDeliveryEndTime")?.disable();
    }

    // Mark form as pristine after loading data
    this.marketplaceForm.markAsPristine();
  }

  private disableDirectCallSource(): void {
    this.marketplaceForm.get("riskAppetite.referralSources.direct-call")?.disable();
  }

  private initializeIndividualEmailRecipientSettings(marketplace: Marketplace): void {
    const individualEmailRecipientSettings: MarketplaceEmailRecipient[] | undefined =
      marketplace.individualEmailRecipientSettings;
    if (individualEmailRecipientSettings && individualEmailRecipientSettings.length > 0) {
      const individualEmailRecipientSettingGroup = this.marketplaceForm.get(
        this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP
      ) as FormGroup;

      // Clear the defaulted individualEmailRecipientSetting states
      Object.keys(individualEmailRecipientSettingGroup.controls).forEach(
        (controlName: string): void => {
          if (controlName.includes("emailAddress")) {
            individualEmailRecipientSettingGroup.get(controlName)?.setValue("");
          }
          if (controlName.includes("enableSmsNotification")) {
            individualEmailRecipientSettingGroup.get(controlName)?.setValue(false);
          }
          if (controlName.includes("notificationSequenceId")) {
            individualEmailRecipientSettingGroup.get(controlName)?.setValue(null);
          }
          if (controlName.includes("sameDeliveryDaysAsMarketplace")) {
            individualEmailRecipientSettingGroup.get(controlName)?.setValue(false);
          }

          this.clearTheCurrentIndividualEmailRecipientDeliveryDaysSettings(
            controlName,
            individualEmailRecipientSettingGroup
          );

          if (controlName.includes("sameDeliveryTimesAsMarketplace")) {
            individualEmailRecipientSettingGroup.get(controlName)?.setValue(false);
          }

          this.clearTheCurrentIndividualEmailRecipientDeliveryTimesSettings(
            controlName,
            individualEmailRecipientSettingGroup
          );
        }
      );

      // Set the individualEmailRecipientSettings
      individualEmailRecipientSettings.forEach((recipient: MarketplaceEmailRecipient): void => {
        const recipientId: number = recipient.id;
        individualEmailRecipientSettingGroup
          .get(`emailAddress-${recipientId}`)
          ?.setValue(recipient.emailAddress);

        individualEmailRecipientSettingGroup
          .get(`enableSmsNotification-${recipientId}`)
          ?.setValue(recipient.enableSmsNotification);

        individualEmailRecipientSettingGroup
          .get(`notificationSequenceId-${recipientId}`)
          ?.setValue(recipient.notificationSequenceId);

        individualEmailRecipientSettingGroup
          .get(`sameDeliveryDaysAsMarketplace-${recipientId}`)
          ?.setValue(recipient.sameDeliveryDaysAsMarketplace);

        this.setTheCurrentIndividualEmailRecipientDeliveryDaysSettings(
          recipient,
          individualEmailRecipientSettingGroup
        );

        individualEmailRecipientSettingGroup
          .get(`sameDeliveryTimesAsMarketplace-${recipientId}`)
          ?.setValue(recipient.sameDeliveryTimesAsMarketplace);

        this.setTheCurrentIndividualEmailRecipientDeliveryTimesSettings(
          recipient,
          individualEmailRecipientSettingGroup
        );
      });
    }
  }

  private clearTheCurrentIndividualEmailRecipientDeliveryDaysSettings(
    controlName: string,
    individualEmailRecipientSettingGroup: FormGroup<any>
  ): void {
    if (controlName.includes("acceptReferralsOnMonday")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
    if (controlName.includes("acceptReferralsOnTuesday")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
    if (controlName.includes("acceptReferralsOnWednesday")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
    if (controlName.includes("acceptReferralsOnThursday")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
    if (controlName.includes("acceptReferralsOnFriday")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
    if (controlName.includes("acceptReferralsOnSaturday")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
    if (controlName.includes("acceptReferralsOnSunday")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
  }

  private clearTheCurrentIndividualEmailRecipientDeliveryTimesSettings(
    controlName: string,
    individualEmailRecipientSettingGroup: FormGroup<any>
  ): void {
    if (controlName.includes("referralDeliveryStartTime")) {
      individualEmailRecipientSettingGroup
        .get(controlName)
        ?.setValue(this.DEFAULT_DELIVERY_START_TIME);
    }
    if (controlName.includes("referralDeliveryEndTime")) {
      individualEmailRecipientSettingGroup
        .get(controlName)
        ?.setValue(this.DEFAULT_DELIVERY_END_TIME);
    }
    if (controlName.includes("referralDeliveryTimezone")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(this.DEFAULT_TIMEZONE);
    }
    if (controlName.includes("accept24hrReferralDelivery")) {
      individualEmailRecipientSettingGroup.get(controlName)?.setValue(true);
    }
  }

  private setTheCurrentIndividualEmailRecipientDeliveryDaysSettings(
    recipient: MarketplaceEmailRecipient,
    individualEmailRecipientSettingGroup: FormGroup<any>
  ): void {
    const recipientId: number = recipient.id;

    individualEmailRecipientSettingGroup
      .get(`acceptReferralsOnMonday-${recipientId}`)
      ?.setValue(recipient.acceptReferralsOnMonday);

    individualEmailRecipientSettingGroup
      .get(`acceptReferralsOnTuesday-${recipientId}`)
      ?.setValue(recipient.acceptReferralsOnTuesday);

    individualEmailRecipientSettingGroup
      .get(`acceptReferralsOnWednesday-${recipientId}`)
      ?.setValue(recipient.acceptReferralsOnWednesday);

    individualEmailRecipientSettingGroup
      .get(`acceptReferralsOnThursday-${recipientId}`)
      ?.setValue(recipient.acceptReferralsOnThursday);

    individualEmailRecipientSettingGroup
      .get(`acceptReferralsOnFriday-${recipientId}`)
      ?.setValue(recipient.acceptReferralsOnFriday);

    individualEmailRecipientSettingGroup
      .get(`acceptReferralsOnSaturday-${recipientId}`)
      ?.setValue(recipient.acceptReferralsOnSaturday);

    individualEmailRecipientSettingGroup
      .get(`acceptReferralsOnSunday-${recipientId}`)
      ?.setValue(recipient.acceptReferralsOnSunday);

    if (recipient.sameDeliveryDaysAsMarketplace) {
      individualEmailRecipientSettingGroup.get(`acceptReferralsOnMonday-${recipientId}`)?.disable();

      individualEmailRecipientSettingGroup
        .get(`acceptReferralsOnTuesday-${recipientId}`)
        ?.disable();

      individualEmailRecipientSettingGroup
        .get(`acceptReferralsOnWednesday-${recipientId}`)
        ?.disable();

      individualEmailRecipientSettingGroup
        .get(`acceptReferralsOnThursday-${recipientId}`)
        ?.disable();

      individualEmailRecipientSettingGroup.get(`acceptReferralsOnFriday-${recipientId}`)?.disable();

      individualEmailRecipientSettingGroup
        .get(`acceptReferralsOnSaturday-${recipientId}`)
        ?.disable();

      individualEmailRecipientSettingGroup.get(`acceptReferralsOnSunday-${recipientId}`)?.disable();
    }
  }

  private setTheCurrentIndividualEmailRecipientDeliveryTimesSettings(
    recipient: MarketplaceEmailRecipient,
    individualEmailRecipientSettingGroup: FormGroup<any>
  ): void {
    const recipientId: number = recipient.id;
    individualEmailRecipientSettingGroup
      .get(`referralDeliveryStartTime-${recipientId}`)
      ?.setValue(recipient.referralDeliveryStartTime);

    individualEmailRecipientSettingGroup
      .get(`referralDeliveryEndTime-${recipientId}`)
      ?.setValue(recipient.referralDeliveryEndTime);

    individualEmailRecipientSettingGroup
      .get(`referralDeliveryTimezone-${recipientId}`)
      ?.setValue(recipient.referralDeliveryTimezone);

    individualEmailRecipientSettingGroup
      .get(`accept24hrReferralDelivery-${recipientId}`)
      ?.setValue(recipient.accept24hrReferralDelivery);

    if (recipient.sameDeliveryTimesAsMarketplace) {
      individualEmailRecipientSettingGroup
        .get(`referralDeliveryStartTime-${recipientId}`)
        ?.disable();
      individualEmailRecipientSettingGroup.get(`referralDeliveryEndTime-${recipientId}`)?.disable();
      individualEmailRecipientSettingGroup
        .get(`referralDeliveryTimezone-${recipientId}`)
        ?.disable();
      individualEmailRecipientSettingGroup
        .get(`accept24hrReferralDelivery-${recipientId}`)
        ?.disable();
    }

    if (recipient.accept24hrReferralDelivery) {
      individualEmailRecipientSettingGroup
        .get(`referralDeliveryStartTime-${recipientId}`)
        ?.disable();
      individualEmailRecipientSettingGroup.get(`referralDeliveryEndTime-${recipientId}`)?.disable();
    }
  }

  private setStateBasedOnFeatureFlags(): void {
    // NOTE: enable or disable UI features here based on a feature flag
    /*
     * EXAMPLE:
     *   if (!this.isPercentModifierFeatureEnabled) {
     *   this.marketplaceForm.get("percentModifier")?.disable();
     *   }
     */
  }

  /**
   * Converts a percentage modifier to a price modifier
   * @param percentModifier - The percentage modifier value
   * @returns The price modifier value to be multiplied by the original price
   */
  public convertPercentModifierToPriceModifier(percentModifier: number): number {
    if (percentModifier === undefined) return 1;
    const value: number = 1 + percentModifier / 100;
    // Using Math.round to ensure proper rounding behavior
    return Math.round(value * 100) / 100;
  }

  /**
   * Converts a price modifier to a percentage modifier
   * @param priceModifier - The price modifier value
   * @returns The percentage modifier value
   */
  public convertPriceModifierToPercentModifier(priceModifier?: number): number {
    if (priceModifier == undefined) return 0;
    return Number(((priceModifier - 1) * 100).toFixed(0));
  }

  protected preventDecimalInput(event: KeyboardEvent): boolean {
    // Prevent the decimal point key
    if (event.key === ".") {
      event.preventDefault();
      return false;
    }
    return true;
  }

  protected limitDigits(event: any, digitLimit: number): void {
    const input: any = event.target;
    if (input?.value.length > digitLimit) {
      input.value = input.value.slice(0, digitLimit);
    }
  }

  protected isFormValid(): boolean {
    // Check Agency ID is valid
    if (this.showAgencyIdNotFoundError) {
      return false;
    }

    // Check marketplace name
    if (this.showMarketplaceNameError) {
      return false;
    }

    // Check default purchasing agent email address
    if (
      this.marketplaceForm.get("agencyId")?.value &&
      !this.marketplaceForm.get("defaultPurchasingAgentEmailAddress")?.value
    ) {
      this.showDefaultPurchasingAgentEmailAddressError = true;
    }

    if (
      this.showDefaultPurchasingAgentEmailAddressError ||
      this.showDefaultPurchasingAgentNotFoundError
    ) {
      return false;
    }

    // Check Delivered Price is valid
    if (this.marketplaceForm.get(this.PRICE_MODE)?.value === this.DELIVERED_PRICE_MODE) {
      const deliveryPriceValue: number | null = this.marketplaceForm.get(
        this.DELIVERED_PRICE_MODE
      )?.value;
      if (deliveryPriceValue == null || deliveryPriceValue == 0) {
        this.showDeliveredPriceError = true;
        return false;
      }
    }

    // Check % Modifier is valid
    if (this.marketplaceForm.get(this.PRICE_MODE)?.value === this.PERCENT_MODIFIER_MODE) {
      const percentModifierValue: number | null = this.marketplaceForm.get(
        this.PERCENT_MODIFIER_MODE
      )?.value;
      if (percentModifierValue == null) {
        this.showPercentModifierError = true;
        return false;
      }

      if (percentModifierValue < 0 && Math.abs(percentModifierValue) > 100) {
        this.showNegativePercentModifierError = true;
        return false;
      }
    }

    // Check if Referral Price Range is valid
    if (
      this.showMinReferralPriceError ||
      this.showMaxReferralPriceError ||
      this.showReferralPriceError
    ) {
      return false;
    }

    // Check if at least one state is selected
    const statesGroup = this.marketplaceForm.get(this.STATE_GROUP) as FormGroup;
    const hasAnyStateSelected: boolean = Object.keys(statesGroup.controls).some(
      (state: string): boolean => statesGroup.get(state)?.value === true
    );

    if (!hasAnyStateSelected) {
      this.showStatesError = true;
      return false;
    }

    // Check if at least one referral source is selected
    const referralSourceGroup = this.marketplaceForm.get(this.REFERRAL_SOURCES_GROUP) as FormGroup;
    const hasAnyReferralSourceSelected: boolean = Object.keys(referralSourceGroup.controls).some(
      (referralSource: string): boolean => referralSourceGroup.get(referralSource)?.value === true
    );

    if (!hasAnyReferralSourceSelected) {
      this.showReferralSourceError = true;
      return false;
    }

    // Check if at least one appetite section is enabled
    if (this.showBusinessLineRequiredError) {
      return false;
    }

    // Check if the number of commercial business lines is valid
    if (
      this.showMinCommercialBusinessLinesError ||
      this.showMaxCommercialBusinessLinesError ||
      this.showCommercialBusinessLinesError
    ) {
      return false;
    }

    // Check if Years in Business is valid
    if (
      this.showMinYearsInBusinessError ||
      this.showMaxYearsInBusinessError ||
      this.showYearsInBusinessError
    ) {
      return false;
    }

    // Check if the Number of Employees is valid
    if (
      this.showMinNumberOfEmployeesError ||
      this.showMaxNumberOfEmployeesError ||
      this.showNumberOfEmployeesError
    ) {
      return false;
    }

    // Check if Commercial Estimated Premium is valid
    if (
      this.showMinCommercialEstimatedPremiumError ||
      this.showMaxCommercialEstimatedPremiumError ||
      this.showCommercialEstimatedPremiumError
    ) {
      return false;
    }

    // Check if the number of personal business lines is valid
    if (
      this.showMinPersonalBusinessLinesError ||
      this.showMaxPersonalBusinessLinesError ||
      this.showPersonalBusinessLinesError
    ) {
      return false;
    }

    // Check if the Personal Estimated Premium is valid
    if (
      this.showMinPersonalEstimatedPremiumError ||
      this.showMaxPersonalEstimatedPremiumError ||
      this.showPersonalEstimatedPremiumError
    ) {
      return false;
    }

    // Check if the number of life & annuities business lines is valid
    if (
      this.showMinLifeAnnuitiesBusinessLinesError ||
      this.showMaxLifeAnnuitiesBusinessLinesError ||
      this.showLifeAnnuitiesBusinessLinesError
    ) {
      return false;
    }

    // Check if Death Benefit is valid
    if (
      this.showMinDeathBenefitError ||
      this.showMaxDeathBenefitError ||
      this.showDeathBenefitError
    ) {
      return false;
    }

    // Check if Referral Delivery Limits are valid
    if (
      this.showReferralsPerHourError ||
      this.showReferralsPerDayError ||
      this.showReferralsPerWeekError ||
      this.showReferralsPerMonthError
    ) {
      return false;
    }

    if (this.showDefaultPurchasingAgentEmailAddressError) {
      return false;
    }

    // Check Email Recipients Format
    if (
      this.emailRecipientsIsInvalid ||
      this.showEmailRecipientError ||
      this.showDuplicateEmailRecipientError
    ) {
      return false;
    }

    if (this.emailBccRecipientsIsInvalid || this.showDuplicateEmailBccRecipientError) {
      return false;
    }

    // Check NAICS format
    if (this.naicsInvalid) {
      return false;
    }

    return !this.naicsInvalid && this.marketplaceForm.valid;
  }

  protected saveMarketplace(): void {
    if (!this.isFormValid()) {
      return;
    }

    this.isSaving = true;
    const formValue: any = this.marketplaceForm.value;
    this.marketplaceForm.disable();
    const naicsMode: string = formValue.riskAppetite.naicsMode;
    const naicsCodes: string[] = formValue.riskAppetite.naicsCodes
      ? formValue.riskAppetite.naicsCodes.split(",").map((code: string): string => code.trim())
      : [];

    const marketplace: Marketplace = this.getUpdatedMarketplace(naicsMode, naicsCodes, formValue);

    if (this.isNewMarketplaceCreationRequest) {
      this.marketplaceService
        .createMarketplace(marketplace)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (marketplace: Marketplace): void => {
            this.isNewMarketplaceCreationRequest = false;
            this.isNewMarketplace = true;
            this.router.navigate(["/admin", "marketplaces"]).finally();
          },
          error: (error: HttpErrorResponse): void => {
            this.toastService.open(
              `Failed to save marketplace [ ${marketplace.marketplaceName} ]. Please refresh the page and try again.`,
              "Close",
              {
                duration: this.TOAST_DURATION,
                panelClass: ["bg-red-600"]
              }
            );
            this.isSaving = false;
            this.marketplaceForm.enable();
          },
          complete: (): void => {}
        });
    } else {
      this.marketplaceService
        .updateMarketplace(marketplace)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (): void => {
            this.loadMarketplace(true);
          },
          error: (error: HttpErrorResponse): void => {
            this.toastService.open(
              `Failed to update marketplace [ ${this.marketplace?.marketplaceName}} ]. Please refresh the page and try again.`,
              "Close",
              {
                duration: this.TOAST_DURATION,
                panelClass: ["bg-red-600"]
              }
            );
            this.isSaving = false;
            this.marketplaceForm.enable();
          },
          complete: (): void => {}
        });
    }
  }

  private getUpdatedMarketplace(
    naicsMode: string,
    naicsCodes: string[],
    formValue: any
  ): Marketplace {
    const statesGroup = this.marketplaceForm.get(this.STATE_GROUP) as FormGroup;
    const referralSourcesGroup = this.marketplaceForm.get(this.REFERRAL_SOURCES_GROUP) as FormGroup;
    const individualEmailRecipientSettingsGroup = this.marketplaceForm.get(
      this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP
    ) as FormGroup;

    const selectedStates: string[] = Object.keys(statesGroup.controls).filter(
      (stateCode: string): boolean => statesGroup.get(stateCode)?.value === true
    );

    const excludedSources: string[] = Object.keys(referralSourcesGroup.controls).filter(
      // Note get([]) with square brackets used as form controls with periods like commercialinsurance.net sometimes has issues in reactive forms
      (excludedSource: string): boolean =>
        referralSourcesGroup.get([excludedSource])?.value === false
    );

    if (this.isNewMarketplaceCreationRequest) {
      this.captureBusinessLineAppetites();
    }

    this.allRiskAppetiteBusinessLines = [
      ...this.commercialRiskAppetiteBusinessLines,
      ...this.personalRiskAppetiteBusinessLines,
      ...this.lifeAnnuitiesRiskAppetiteBusinessLines
    ];

    const individualEmailRecipientSettings: MarketplaceEmailRecipient[] =
      this.extractIndividualEmailRecipientSettings(individualEmailRecipientSettingsGroup);

    return {
      id: this.isNewMarketplaceCreationRequest ? undefined : this.marketplace?.id,
      marketplaceName: formValue.marketplaceName ? formValue.marketplaceName.trim() : null,
      generic: formValue.generic ?? false,
      referralDeliveryMethod:
        formValue.referralDeliveryMethod ?? ReferralDeliveryMethodEnum.GENERIC,
      agencyId: formValue.agencyId ?? null,
      defaultPurchasingAgentEmailAddress: formValue.defaultPurchasingAgentEmailAddress ?? null,
      premierPartnerMarketplace: formValue.premierPartnerMarketplace ?? false,
      billed: this.paymentOption == this.paymentOptionEnum.BILLED,
      debitPrepaidBalance: this.paymentOption == this.paymentOptionEnum.DEBIT_PREPAID_BALANCE,
      legalEntityName: formValue.legalEntityName ? formValue.legalEntityName.trim() : null,
      legalEntityUrl: formValue.legalEntityUrl ? formValue.legalEntityUrl.trim() : null,
      phoneNumber: formValue.phoneNumber ? formValue.phoneNumber.trim() : null,
      priority: this.marketplace?.priority,
      includedInMarketplaceFlow: this.marketplace?.includedInMarketplaceFlow ?? true,
      riskAppetite: {
        riskAppetiteId: this.marketplace?.riskAppetite?.riskAppetiteId,
        riskAppetiteBusinessLines: this.allRiskAppetiteBusinessLines ?? [],
        excludedNaics:
          naicsMode.toLowerCase() === "exclude"
            ? naicsCodes
            : (this.getExcludeNaicsCodes(naicsCodes) ?? []),
        excludedSources: excludedSources ?? [],
        stateCodes: selectedStates ?? [],
        acceptPersonalLines: formValue.riskAppetite?.acceptPersonalLines ?? false,
        acceptCommercialLines: formValue.riskAppetite?.acceptCommercialLines ?? false,
        acceptLifeAnnuitiesLines: formValue.riskAppetite?.acceptLifeAnnuitiesLines ?? false,
        personalBusinessLinesMin: formValue.riskAppetite?.personalBusinessLinesMin ?? null,
        personalBusinessLinesMax: formValue.riskAppetite?.personalBusinessLinesMax ?? null,
        personalEstimatedPremiumMin: formValue.riskAppetite?.personalEstimatedPremiumMin ?? null,
        personalEstimatedPremiumMax: formValue.riskAppetite?.personalEstimatedPremiumMax ?? null,
        commercialBusinessLinesMin: formValue.riskAppetite?.commercialBusinessLinesMin ?? null,
        commercialBusinessLinesMax: formValue.riskAppetite?.commercialBusinessLinesMax ?? null,
        commercialEstimatedPremiumMin:
          formValue.riskAppetite?.commercialEstimatedPremiumMin ?? null,
        commercialEstimatedPremiumMax:
          formValue.riskAppetite?.commercialEstimatedPremiumMax ?? null,
        acceptPersonalInsured: formValue.riskAppetite?.acceptPersonalInsured ?? false,
        acceptPersonalUninsured: formValue.riskAppetite?.acceptPersonalUninsured ?? false,
        acceptCommercialInsured: formValue.riskAppetite?.acceptCommercialInsured ?? false,
        acceptCommercialUninsured: formValue.riskAppetite?.acceptCommercialUninsured ?? false,
        numberOfEmployeesMin: formValue.riskAppetite?.numberOfEmployeesMin ?? null,
        numberOfEmployeesMax: formValue.riskAppetite?.numberOfEmployeesMax ?? null,
        yearsInBusinessMin: formValue.riskAppetite?.yearsInBusinessMin ?? null,
        yearsInBusinessMax: formValue.riskAppetite?.yearsInBusinessMax ?? null,
        lifeAnnuitiesBusinessLinesMin:
          formValue.riskAppetite?.lifeAnnuitiesBusinessLinesMin ?? null,
        lifeAnnuitiesBusinessLinesMax:
          formValue.riskAppetite?.lifeAnnuitiesBusinessLinesMax ?? null,
        deathBenefitMin: formValue.riskAppetite?.deathBenefitMin ?? null,
        deathBenefitMax: formValue.riskAppetite?.deathBenefitMax ?? null,
        acceptMedicalExamConsent: formValue.riskAppetite?.acceptMedicalExamConsent ?? false,
        acceptMedicalExamRefusal: formValue.riskAppetite?.acceptMedicalExamRefusal ?? false,
        acceptTobaccoUsePastYear: formValue.riskAppetite?.acceptTobaccoUsePastYear ?? false,
        acceptNoTobaccoUsePastYear: formValue.riskAppetite?.acceptNoTobaccoUsePastYear ?? false,
        firstNameRequired: formValue.riskAppetite?.firstNameRequired ?? false,
        lastNameRequired: formValue.riskAppetite?.lastNameRequired ?? false,
        emailAddressRequired: formValue.riskAppetite?.emailAddressRequired ?? false,
        phoneNumberRequired: formValue.riskAppetite?.phoneNumberRequired ?? false,
        cityRequired: formValue.riskAppetite?.cityRequired ?? false,
        stateRequired: formValue.riskAppetite?.stateRequired ?? false,
        postalCodeRequired: formValue.riskAppetite?.postalCodeRequired ?? false,
        businessAddressRequired: formValue.riskAppetite?.businessAddressRequired ?? false,
        businessNameRequired: formValue.riskAppetite?.businessNameRequired ?? false,
        journayaIdRequired: formValue.riskAppetite?.journayaIdRequired ?? false,
        businessLineRequired: formValue.riskAppetite?.businessLineRequired ?? false,
        maxReferralsPerHour: formValue.riskAppetite?.maxReferralsPerHour ?? null,
        maxReferralsPerDay: formValue.riskAppetite?.maxReferralsPerDay ?? null,
        maxReferralsPerWeek: formValue.riskAppetite?.maxReferralsPerWeek ?? null,
        maxReferralsPerMonth: formValue.riskAppetite?.maxReferralsPerMonth ?? null,
        acceptReferralsOnMonday: formValue.riskAppetite?.acceptReferralsOnMonday ?? false,
        acceptReferralsOnTuesday: formValue.riskAppetite?.acceptReferralsOnTuesday ?? false,
        acceptReferralsOnWednesday: formValue.riskAppetite?.acceptReferralsOnWednesday ?? false,
        acceptReferralsOnThursday: formValue.riskAppetite?.acceptReferralsOnThursday ?? false,
        acceptReferralsOnFriday: formValue.riskAppetite?.acceptReferralsOnFriday ?? false,
        acceptReferralsOnSaturday: formValue.riskAppetite?.acceptReferralsOnSaturday ?? false,
        acceptReferralsOnSunday: formValue.riskAppetite?.acceptReferralsOnSunday ?? false,
        acceptReferralsOnBankHolidays:
          formValue.riskAppetite?.acceptReferralsOnBankHolidays ?? false,
        suppressedHolidays: this.suppressedHolidays,
        customHolidays: this.customHolidays,
        referralDeliveryStartTime: formValue.riskAppetite?.accept24hrReferralDelivery
          ? this.DEFAULT_DELIVERY_START_TIME
          : (formValue.riskAppetite?.referralDeliveryStartTime ?? null),
        referralDeliveryEndTime: formValue.riskAppetite?.accept24hrReferralDelivery
          ? this.DEFAULT_DELIVERY_END_TIME
          : (formValue.riskAppetite?.referralDeliveryEndTime ?? null),
        accept24hrReferralDelivery: formValue.riskAppetite?.accept24hrReferralDelivery ?? false,
        referralDeliveryTimezone: formValue.riskAppetite?.referralDeliveryTimezone ?? null,
        acceptWarmTransfers: formValue.riskAppetite?.acceptWarmTransfers ?? false,
        referralPriceMin: formValue.riskAppetite?.referralPriceMin ?? null,
        referralPriceMax: formValue.riskAppetite?.referralPriceMax ?? null
      },
      oneToOneConsentRequired: formValue.riskAppetite?.oneToOneConsentRequired ?? false,
      capturesOwnConsumerConsent: formValue.riskAppetite?.capturesOwnConsumerConsent ?? false,
      acceptIneligible: formValue.riskAppetite?.acceptIneligible ?? false,
      enabled: this.marketplace?.enabled ?? false,
      applyDeliveredPrice: this.marketplaceTypeEnum.INSTANT
        ? formValue.priceMode
          ? formValue.priceMode == this.DELIVERED_PRICE_MODE
          : false
        : false,
      deliveredPrice: this.marketplaceTypeEnum.INSTANT ? (formValue.deliveredPrice ?? null) : null,
      priceModifier: this.marketplaceTypeEnum.DURATION
        ? (this.convertPercentModifierToPriceModifier(formValue.percentModifier) ?? null)
        : undefined,
      type: formValue.marketplaceType ?? null,
      deliverToApi: formValue.deliverToApi ?? false,
      deliveryApiUrl: formValue.deliveryApiUrl ?? null,
      deliveryApiKey: formValue.deliveryApiKey ?? null,
      deliveryApiType: formValue.deliveryApiType,
      deliverToEmailAddress: formValue.deliverToEmailAddress ?? false,
      emailTemplate: formValue.emailTemplate ?? EmailTemplateEnum.DEFAULT,
      enableRoundRobinDelivery: formValue.enableRoundRobinDelivery ?? false,
      enableIndividualEmailDeliverySettings:
        !formValue.emailRecipients || formValue.emailRecipients.trim() === ""
          ? false
          : (formValue.enableIndividualEmailDeliverySettings ?? false),
      emailRecipients: formValue.emailRecipients
        ? formValue.emailRecipients
            .split(",")
            .map((emailAddress: string): string => emailAddress.trim())
        : [],
      emailBccRecipients: formValue.emailBccRecipients
        ? formValue.emailBccRecipients
            .split(",")
            .map((emailAddress: string): string => emailAddress.trim())
        : [],
      individualEmailRecipientSettings: individualEmailRecipientSettings ?? []
    };
  }

  private captureBusinessLineAppetites(): void {
    // Capture the Business Line Appetites
    const riskAppetiteId: number | undefined = this.marketplace?.riskAppetite?.riskAppetiteId;
    const commercialBusinessLineStatusMap: Map<string, BusinessLineAppetiteEnum> =
      this.commercialBusinessLineAppetiteComponent?.getAllBusinessLinesAppetites();
    if (commercialBusinessLineStatusMap) {
      this.commercialRiskAppetiteBusinessLines = [];
      commercialBusinessLineStatusMap.forEach(
        (businessLineAppetite: BusinessLineAppetiteEnum, businessLineCode: string): void => {
          if (businessLineAppetite !== BusinessLineAppetiteEnum.OPTIONAL) {
            this.commercialRiskAppetiteBusinessLines.push({
              riskAppetiteId: riskAppetiteId,
              businessLineCode: businessLineCode,
              businessLineCategory: BusinessLineCategoryEnum.COMMERCIAL,
              excluded: businessLineAppetite === BusinessLineAppetiteEnum.EXCLUDED
            });
          }
        }
      );
    }

    const personalBusinessLineStatusMap: Map<string, BusinessLineAppetiteEnum> =
      this.personalBusinessLineAppetiteComponent?.getAllBusinessLinesAppetites();
    if (personalBusinessLineStatusMap) {
      this.personalRiskAppetiteBusinessLines = [];
      personalBusinessLineStatusMap.forEach(
        (businessLineAppetite: BusinessLineAppetiteEnum, businessLineCode: string): void => {
          if (businessLineAppetite !== BusinessLineAppetiteEnum.OPTIONAL) {
            this.personalRiskAppetiteBusinessLines.push({
              riskAppetiteId: riskAppetiteId,
              businessLineCode: businessLineCode,
              businessLineCategory: BusinessLineCategoryEnum.PERSONAL,
              excluded: businessLineAppetite === BusinessLineAppetiteEnum.EXCLUDED
            });
          }
        }
      );
    }

    const lifeAnnuitiesBusinessLineStatusMap: Map<string, BusinessLineAppetiteEnum> =
      this.lifeAnnuitiesBusinessLineAppetiteComponent?.getAllBusinessLinesAppetites();
    if (lifeAnnuitiesBusinessLineStatusMap) {
      this.lifeAnnuitiesRiskAppetiteBusinessLines = [];
      lifeAnnuitiesBusinessLineStatusMap.forEach(
        (businessLineAppetite: BusinessLineAppetiteEnum, businessLineCode: string): void => {
          if (businessLineAppetite !== BusinessLineAppetiteEnum.OPTIONAL) {
            this.lifeAnnuitiesRiskAppetiteBusinessLines.push({
              riskAppetiteId: riskAppetiteId,
              businessLineCode: businessLineCode,
              businessLineCategory: BusinessLineCategoryEnum.LIFE_ANNUITIES,
              excluded: businessLineAppetite === BusinessLineAppetiteEnum.EXCLUDED
            });
          }
        }
      );
    }
  }

  private extractIndividualEmailRecipientSettings(
    formControlGroup: FormGroup
  ): MarketplaceEmailRecipient[] {
    const individualEmailRecipientSettings: MarketplaceEmailRecipient[] = [];
    const controlNames: string[] = Object.keys(formControlGroup.controls);

    // Get unique recipient IDs by extracting IDs from control names
    const recipientIds: Set<string> = new Set();
    controlNames.forEach((controlName: string): void => {
      const matchHyphenFollowedByNumber: RegExpMatchArray | null = controlName.match(/-(\d+)$/);
      if (matchHyphenFollowedByNumber) {
        // matchHyphenFollowedByNumber returns in the form ["-123", "123"]
        const recipientId: string = matchHyphenFollowedByNumber[1];
        recipientIds.add(recipientId);
      }
    });

    // For each unique individual email recipient (recipient ID), extract all their settings
    recipientIds.forEach((recipientId: string): void => {
      if (this.marketplaceId) {
        const recipient: MarketplaceEmailRecipient = {
          id: Number(recipientId),
          marketplaceId: this.marketplaceId,
          emailAddress: formControlGroup.get(`emailAddress-${recipientId}`)?.value,
          enableSmsNotification:
            formControlGroup.get(`enableSmsNotification-${recipientId}`)?.value || false,
          notificationSequenceId: formControlGroup.get(`notificationSequenceId-${recipientId}`)
            ?.value,
          sameDeliveryDaysAsMarketplace:
            formControlGroup.get(`sameDeliveryDaysAsMarketplace-${recipientId}`)?.value || false,
          acceptReferralsOnMonday:
            formControlGroup.get(`acceptReferralsOnMonday-${recipientId}`)?.value || false,
          acceptReferralsOnTuesday:
            formControlGroup.get(`acceptReferralsOnTuesday-${recipientId}`)?.value || false,
          acceptReferralsOnWednesday:
            formControlGroup.get(`acceptReferralsOnWednesday-${recipientId}`)?.value || false,
          acceptReferralsOnThursday:
            formControlGroup.get(`acceptReferralsOnThursday-${recipientId}`)?.value || false,
          acceptReferralsOnFriday:
            formControlGroup.get(`acceptReferralsOnFriday-${recipientId}`)?.value || false,
          acceptReferralsOnSaturday:
            formControlGroup.get(`acceptReferralsOnSaturday-${recipientId}`)?.value || false,
          acceptReferralsOnSunday:
            formControlGroup.get(`acceptReferralsOnSunday-${recipientId}`)?.value || false,
          sameDeliveryTimesAsMarketplace: formControlGroup.get(
            `sameDeliveryTimesAsMarketplace-${recipientId}`
          )?.value,
          referralDeliveryStartTime:
            formControlGroup.get(`referralDeliveryStartTime-${recipientId}`)?.value ||
            this.DEFAULT_DELIVERY_START_TIME,
          referralDeliveryEndTime:
            formControlGroup.get(`referralDeliveryEndTime-${recipientId}`)?.value ||
            this.DEFAULT_DELIVERY_END_TIME,
          accept24hrReferralDelivery:
            formControlGroup.get(`accept24hrReferralDelivery-${recipientId}`)?.value || false,
          referralDeliveryTimezone:
            formControlGroup.get(`referralDeliveryTimezone-${recipientId}`)?.value ||
            this.DEFAULT_TIMEZONE
        };

        individualEmailRecipientSettings.push(recipient);
      }
    });

    return individualEmailRecipientSettings;
  }

  private getExcludeNaicsCodes(naicsCodes: string[]): string[] {
    return this.allNaicsCodes
      .filter((naicsCode: string | null): naicsCode is string => naicsCode !== null)
      .filter((naicsCode: string): boolean => !naicsCodes.includes(naicsCode));
  }

  protected discardChanges(): void {
    if (this.marketplaceForm.dirty) {
      this.isLoading = true;
      this.loadMarketplace(false);
    }
  }

  private handleFormValueChanges(): void {
    this.handleMarketplaceTypeValueChanges();
    this.handleAgencyIdValueChanges();
    this.handleMarketplaceNameChanges();
    this.handleDefaultPurchasingAgentEmailAddressValueChanges();
    this.handlePaymentOptionChanges();
    this.handlePriceModeChanges();
    this.handleReferralPriceAdjustmentValueChanges();
    this.handleReferralPriceRangeValueChanges();
    this.handleReferralSourceValueChanges();
    this.handleBusinessLineRequiredValueChanges();
    this.handleStateValueChanges();
    this.handleCommercialAppetiteValueChanges();
    this.handleNaicsModeValueChanges();
    this.handleNaicsCodeValueChanges();
    this.handleNumberOfCommercialBusinessLinesValueChanges();
    this.handleYearsInBusinessValueChanges();
    this.handleNumberOfEmployeesValueChanges();
    this.handleCommercialEstimatedPremiumValueChanges();
    this.handlePersonalAppetiteValueChanges();
    this.handleNumberOfPersonalBusinessLinesValueChanges();
    this.handlePersonalEstimatedPremiumValueChanges();
    this.handleLifeAndAnnuitiesAppetiteValueChanges();
    this.handleNumberOfLifeAndAnnuitiesBusinessLinesValueChanges();
    this.handleDeathBenefitValueChanges();
    this.handleReferralDeliveryLimitsValueChanges();
    this.handleMarketplaceReferralDeliveryDayError();
    this.handleMarketplaceDeliveryDaysValueChanges();
    this.handleMarketplaceDeliveryTimesValueChanges();
    this.handleMarketplace24hrDeliveryValueChanges();
    this.handleApiDeliveryValueChanges();
    this.handleEmailDeliveryValueChanges();
    this.handleEmailRecipientValueChanges();
    this.handleIndividualRecipientDeliverySettingsChanges();
    this.handleMatchDeliveryDaysValueChanges();
    this.handleMatchDeliveryTimesValueChanges();
    this.handleIndividualRecipient24hrDeliveryValueChanges();
    this.handleEmailBccRecipientValueChanges();
    this.handlePremierPartnerMarketplaceValueChanges();
  }

  private handleMarketplaceTypeValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("marketplaceType")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((marketplaceType: string): void => {
        if (marketplaceType === this.marketplaceTypeEnum.INSTANT) {
          this.marketplaceType = this.marketplaceTypeEnum.INSTANT;
        } else if (marketplaceType === this.marketplaceTypeEnum.DURATION) {
          this.marketplaceType = this.marketplaceTypeEnum.DURATION;
        }
      });
  }

  private handleAgencyIdValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("agencyId")
      ?.valueChanges.pipe(
        debounceTime(1000), // Wait 1000ms after the user stops typing
        takeUntil(this.onDestroy$)
      )
      .subscribe((agencyId: number): void => {
        this.showAgencyIdNotFoundError = false;
        if (agencyId) {
          this.getAgencyInfo(agencyId);
        } else {
          this.isAgencyFound = false;
          this.agency = undefined;
          if (this.marketplaceForm.touched && !this.isSaving) {
            this.marketplaceForm.patchValue({
              marketplaceName: "",
              legalEntityName: "",
              phoneNumber: ""
            });
          }
        }
      });
  }

  private getAgencyInfo(agencyId: number): void {
    this.showAgencyIdSpinner = true;
    this.agencyService
      .getAgency(agencyId)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (agencyPage: Page<Agency>): void => {
          if (agencyPage.content.length > 0) {
            this.showAgencyIdNotFoundError = false;
            this.isAgencyFound = true;
            this.agency = agencyPage.content.at(0);
            if (this.agency && this.isNewMarketplaceCreationRequest) {
              this.marketplaceForm.patchValue({
                marketplaceName: this.agency.name,
                legalEntityName: this.agency.name,
                phoneNumber: this.agency.contact?.phone
              });
            }
          } else {
            this.showAgencyIdNotFoundError = true;
            this.isAgencyFound = false;
            this.agency = undefined;
            this.marketplaceForm.patchValue({
              marketplaceName: "",
              legalEntityName: "",
              phoneNumber: ""
            });
          }
          this.showAgencyIdSpinner = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            `Failed to retrieve agency ID [${agencyId}]. Please refresh the page and try again.`,
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
          this.showAgencyIdSpinner = false;
        }
      });
  }

  private handleMarketplaceNameChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("marketplaceName")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((marketplaceName: string): void => {
        this.showMarketplaceNameError = !marketplaceName.trim();
      });
  }

  private handleDefaultPurchasingAgentEmailAddressValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("defaultPurchasingAgentEmailAddress")
      ?.valueChanges.pipe(
        debounceTime(1000), // Wait 1000ms after the user stops typing
        takeUntil(this.onDestroy$)
      )
      .subscribe((emailAddress: string): void => {
        this.showDefaultPurchasingAgentEmailAddressError = false;
        this.showDefaultPurchasingAgentNotFoundError = false;
        if (emailAddress) {
          if (this.isValidEmailAddress(emailAddress)) {
            this.getDefaultPurchaser(emailAddress);
          } else {
            this.showDefaultPurchasingAgentEmailAddressError = true;
          }
        } else {
          this.isDefaultPurchasingAgentFound = false;
          this.agency = undefined;
        }
      });
  }

  private isValidEmailAddress(emailAddress: string | null | undefined): boolean {
    if (!emailAddress || emailAddress.trim() === "") {
      return false;
    }

    const validEmailAddressPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]{2,}\.[A-Za-z]{2,}$/;
    return validEmailAddressPattern.test(emailAddress.trim());
  }

  private getDefaultPurchaser(emailAddress: string): void {
    this.showDefaultPurchasingAgentSpinner = true;
    this.userService
      .getIwUser(emailAddress)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (iwUserPage: Page<IwUser>): void => {
          if (iwUserPage.content.length === 1) {
            this.showDefaultPurchasingAgentNotFoundError = false;
            this.isDefaultPurchasingAgentFound = true;
            this.defaultPurchasingAgent = iwUserPage.content.at(0);
          } else {
            this.showDefaultPurchasingAgentNotFoundError = true;
            this.isDefaultPurchasingAgentFound = false;
            this.defaultPurchasingAgent = undefined;
          }
          this.showDefaultPurchasingAgentSpinner = false;
        },
        error: (error: HttpErrorResponse): void => {
          this.toastService.open(
            `Failed to retrieve default purchaser [${emailAddress}]. Please refresh the page and try again.`,
            "Close",
            {
              duration: this.TOAST_DURATION,
              panelClass: ["bg-red-600"]
            }
          );
          this.showDefaultPurchasingAgentSpinner = false;
        }
      });
  }

  private handlePaymentOptionChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("paymentOption")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((paymentOption: string): void => {
        this.paymentOption = paymentOption;
        if (paymentOption == this.paymentOptionEnum.DEBIT_PREPAID_BALANCE) {
          this.marketplaceForm.get("deliverToEmailAddress")?.setValue(true);

          if (!this.isNewMarketplaceCreationRequest) {
            this.marketplaceForm.get("enableIndividualEmailDeliverySettings")?.setValue(true);
          }
        } else {
          this.showDefaultPurchasingAgentEmailAddressError = false;
        }
      });
  }

  private handlePriceModeChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get(this.PRICE_MODE)
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((priceMode: string): void => {
        if (priceMode === this.DELIVERED_PRICE_MODE) {
          this.showPercentModifierError = false;
          this.showNegativePercentModifierError = false;
        }

        if (priceMode === this.PERCENT_MODIFIER_MODE) {
          this.showDeliveredPriceError = false;
        }
      });
  }

  private handleReferralPriceAdjustmentValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get(this.DELIVERED_PRICE_MODE)
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((deliveredPrice: number): void => {
        if (this.marketplaceForm.get(this.PRICE_MODE)?.value === this.DELIVERED_PRICE_MODE) {
          this.showDeliveredPriceError = deliveredPrice == null || deliveredPrice == 0;
        }
      });

    this.marketplaceForm
      .get(this.PERCENT_MODIFIER_MODE)
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((percentModifier: number): void => {
        if (this.marketplaceForm.get(this.PRICE_MODE)?.value === this.PERCENT_MODIFIER_MODE) {
          this.showPercentModifierError = percentModifier == null;
          this.showNegativePercentModifierError =
            percentModifier < 0 && Math.abs(percentModifier) > 100;
        }
      });
  }

  private handleReferralPriceRangeValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min. Price
    this.marketplaceForm
      .get("riskAppetite.referralPriceMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((minPrice: number): void => {
        if (minPrice === null || minPrice === undefined) {
          this.showMinReferralPriceError = false;
          this.showReferralPriceError = false;
          return;
        }

        if (minPrice < 0) {
          this.showMinReferralPriceError = true;
          this.showReferralPriceError = false;
        } else {
          const maxPrice: number = this.marketplaceForm.get("riskAppetite.referralPriceMax")?.value;
          if (maxPrice === null || maxPrice === undefined) {
            this.showMinReferralPriceError = false;
            this.showReferralPriceError = false;
            return;
          }
          if (minPrice > maxPrice) {
            this.showMinReferralPriceError = false;
            this.showReferralPriceError = true;
          } else {
            this.showMinReferralPriceError = false;
            this.showReferralPriceError = false;
          }
        }
      });

    // Max. Price
    this.marketplaceForm
      .get("riskAppetite.referralPriceMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((maxPrice: number): void => {
        if (maxPrice === null || maxPrice === undefined) {
          this.showMaxReferralPriceError = false;
          this.showReferralPriceError = false;
          return;
        }

        if (maxPrice < 0) {
          this.showMaxReferralPriceError = true;
          this.showReferralPriceError = false;
        } else {
          const minPrice: number = this.marketplaceForm.get("riskAppetite.referralPriceMin")?.value;
          if (minPrice === null || minPrice === undefined) {
            this.showMaxReferralPriceError = false;
            this.showReferralPriceError = false;
            return;
          }
          if (minPrice > maxPrice) {
            this.showMaxReferralPriceError = false;
            this.showReferralPriceError = true;
          } else {
            this.showMaxReferralPriceError = false;
            this.showReferralPriceError = false;
          }
        }
      });
  }

  /**
   * Determines if at least one referral source is selected and shows the error message if not
   */
  private handleReferralSourceValueChanges(): void {
    if (!this.marketplaceForm) return;

    const referralSourceGroup = this.marketplaceForm.get(this.REFERRAL_SOURCES_GROUP) as FormGroup;
    referralSourceGroup.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((referralSources: any): void => {
        this.showReferralSourceError = !Object.values(referralSources).some(
          (referralSource: any): boolean => referralSource === true
        );
      });
  }

  protected hasNoDaysSelectedError(recipientId: number): boolean {
    const formGroup: AbstractControl<any, any> | null = this.marketplaceForm.get(
      this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP
    );
    if (!formGroup || !formGroup.errors) {
      return false;
    }

    const errorKey = `noDeliveryDaySelected-${recipientId}`;
    return formGroup.errors[errorKey] !== undefined;
  }

  private updateIndividualEmailRecipientSettings(): void {
    if (!this.marketplaceForm) return;

    const individualEmailRecipientSettingGroup = this.marketplaceForm.get(
      this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP
    ) as FormGroup;

    Object.keys(individualEmailRecipientSettingGroup.controls).forEach(
      (controlName: string): void => {
        if (
          controlName.startsWith("sameDeliveryDaysAsMarketplace-") &&
          individualEmailRecipientSettingGroup.get(controlName)?.value
        ) {
          const recipientId: number = Number(
            controlName.split("sameDeliveryDaysAsMarketplace-")[1]
          );
          this.setIndividualEmailRecipientDeliveryDaysInfoToMatchMarketplace(recipientId);
        }

        if (
          controlName.startsWith("sameDeliveryTimesAsMarketplace-") &&
          individualEmailRecipientSettingGroup.get(controlName)?.value
        ) {
          const recipientId: number = Number(
            controlName.split("sameDeliveryTimesAsMarketplace-")[1]
          );
          this.setIndividualEmailRecipientDeliveryTimesInfoToMatchMarketplace(recipientId);
        }
      }
    );
  }

  private handleBusinessLineRequiredValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("riskAppetite.businessLineRequired")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((businessLineRequired: boolean): void => {
        if (businessLineRequired) {
          this.showBusinessLineError();
        } else {
          this.showBusinessLineRequiredError = false;
        }
      });
  }

  /**
   * Determines if at least one state is selected and shows the error message if not
   */
  private handleStateValueChanges(): void {
    if (!this.marketplaceForm) return;

    const stateGroup = this.marketplaceForm.get(this.STATE_GROUP) as FormGroup;
    stateGroup.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe((states: any): void => {
      this.showStatesError = !Object.values(states).some((state: any): boolean => state === true);
    });
  }

  private handleCommercialAppetiteValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("riskAppetite.acceptCommercialLines")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((acceptCommercialLines: boolean): void => {
        if (acceptCommercialLines) {
          this.showBusinessLineRequiredError = false;
        } else {
          this.showBusinessLineError();
        }
      });
  }

  private handleNaicsCodeValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("riskAppetite.naicsCodes")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((naicsCodes: string): void => {
        this.validateNaicsCodes(naicsCodes);
      });
  }

  private validateNaicsCodes(naicsCodes: string | null | undefined): void {
    const commercialEnabled: boolean | null = this.marketplaceForm.get(
      "riskAppetite.acceptCommercialLines"
    )?.value;
    if (!commercialEnabled) {
      this.naicsInvalid = false;
      return;
    }

    if (!naicsCodes || naicsCodes.trim() === "") {
      this.naicsInvalid = false;
      return;
    }

    const sixDigitsAndCommaSeperatedPattern = /^(\d{6})(,\s*\d{6})*$/;
    this.naicsInvalid = !sixDigitsAndCommaSeperatedPattern.test(naicsCodes);
  }

  private handleNumberOfCommercialBusinessLinesValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Commercial Business Lines
    this.marketplaceForm
      .get("riskAppetite.commercialBusinessLinesMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((commercialBusinessLinesMin: number): void => {
        if (commercialBusinessLinesMin === null || commercialBusinessLinesMin === undefined) {
          this.showMinCommercialBusinessLinesError = false;
          this.showCommercialBusinessLinesError = false;
          return;
        }

        if (commercialBusinessLinesMin < 0) {
          this.showMinCommercialBusinessLinesError = true;
          this.showCommercialBusinessLinesError = false;
        } else {
          const commercialBusinessLinesMax: number = this.marketplaceForm.get(
            "riskAppetite.commercialBusinessLinesMax"
          )?.value;
          if (commercialBusinessLinesMax === null || commercialBusinessLinesMax === undefined) {
            this.showMinCommercialBusinessLinesError = false;
            this.showCommercialBusinessLinesError = false;
            return;
          }
          if (commercialBusinessLinesMin > commercialBusinessLinesMax) {
            this.showMinCommercialBusinessLinesError = false;
            this.showCommercialBusinessLinesError = true;
          } else {
            this.showMinCommercialBusinessLinesError = false;
            this.showCommercialBusinessLinesError = false;
          }
        }
      });

    // Max Commercial Business Lines
    this.marketplaceForm
      .get("riskAppetite.commercialBusinessLinesMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((commercialBusinessLinesMax: number): void => {
        if (commercialBusinessLinesMax === null || commercialBusinessLinesMax === undefined) {
          this.showMaxCommercialBusinessLinesError = false;
          this.showCommercialBusinessLinesError = false;
          return;
        }

        if (commercialBusinessLinesMax < 0) {
          this.showMaxCommercialBusinessLinesError = true;
          this.showCommercialBusinessLinesError = false;
        } else {
          const commercialBusinessLinesMin: number = this.marketplaceForm.get(
            "riskAppetite.commercialBusinessLinesMin"
          )?.value;
          if (commercialBusinessLinesMin === null || commercialBusinessLinesMin === undefined) {
            this.showMaxCommercialBusinessLinesError = false;
            this.showCommercialBusinessLinesError = false;
            return;
          }
          if (commercialBusinessLinesMin > commercialBusinessLinesMax) {
            this.showMaxCommercialBusinessLinesError = false;
            this.showCommercialBusinessLinesError = true;
          } else {
            this.showCommercialBusinessLinesError = false;
            this.showCommercialBusinessLinesError = false;
          }
        }
      });
  }

  private handleYearsInBusinessValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Years in Business
    this.marketplaceForm
      .get("riskAppetite.yearsInBusinessMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((yearsInBusinessMin: number): void => {
        if (yearsInBusinessMin === null || yearsInBusinessMin === undefined) {
          this.showMinYearsInBusinessError = false;
          this.showYearsInBusinessError = false;
          return;
        }

        if (yearsInBusinessMin < 0) {
          this.showMinYearsInBusinessError = true;
          this.showYearsInBusinessError = false;
        } else {
          const yearsInBusinessMax: number = this.marketplaceForm.get(
            "riskAppetite.yearsInBusinessMax"
          )?.value;
          if (yearsInBusinessMax === null || yearsInBusinessMax === undefined) {
            this.showMinYearsInBusinessError = false;
            this.showYearsInBusinessError = false;
            return;
          }
          if (yearsInBusinessMin > yearsInBusinessMax) {
            this.showMinYearsInBusinessError = false;
            this.showYearsInBusinessError = true;
          } else {
            this.showMinYearsInBusinessError = false;
            this.showYearsInBusinessError = false;
          }
        }
      });

    // Max Years in Business
    this.marketplaceForm
      .get("riskAppetite.yearsInBusinessMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((yearsInBusinessMax: number): void => {
        if (yearsInBusinessMax === null || yearsInBusinessMax === undefined) {
          this.showMaxYearsInBusinessError = false;
          this.showYearsInBusinessError = false;
          return;
        }

        if (yearsInBusinessMax < 0) {
          this.showMaxYearsInBusinessError = true;
          this.showYearsInBusinessError = false;
        } else {
          const yearsInBusinessMin: number = this.marketplaceForm.get(
            "riskAppetite.yearsInBusinessMin"
          )?.value;
          if (yearsInBusinessMin === null || yearsInBusinessMin === undefined) {
            this.showMaxYearsInBusinessError = false;
            this.showYearsInBusinessError = false;
            return;
          }
          if (yearsInBusinessMin > yearsInBusinessMax) {
            this.showMaxYearsInBusinessError = false;
            this.showYearsInBusinessError = true;
          } else {
            this.showMaxYearsInBusinessError = false;
            this.showYearsInBusinessError = false;
          }
        }
      });
  }

  private handleNumberOfEmployeesValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Number of Employees
    this.marketplaceForm
      .get("riskAppetite.numberOfEmployeesMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((numberOfEmployeesMin: number): void => {
        if (numberOfEmployeesMin === null || numberOfEmployeesMin === undefined) {
          this.showMinNumberOfEmployeesError = false;
          this.showNumberOfEmployeesError = false;
          return;
        }

        if (numberOfEmployeesMin < 0) {
          this.showMinNumberOfEmployeesError = true;
          this.showNumberOfEmployeesError = false;
        } else {
          const numberOfEmployeesMax: number = this.marketplaceForm.get(
            "riskAppetite.numberOfEmployeesMax"
          )?.value;
          if (numberOfEmployeesMax === null || numberOfEmployeesMax === undefined) {
            this.showMinNumberOfEmployeesError = false;
            this.showNumberOfEmployeesError = false;
            return;
          }
          if (numberOfEmployeesMin > numberOfEmployeesMax) {
            this.showMinNumberOfEmployeesError = false;
            this.showNumberOfEmployeesError = true;
          } else {
            this.showMinNumberOfEmployeesError = false;
            this.showNumberOfEmployeesError = false;
          }
        }
      });

    // Max Number of Employees
    this.marketplaceForm
      .get("riskAppetite.numberOfEmployeesMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((numberOfEmployeesMax: number): void => {
        if (numberOfEmployeesMax === null || numberOfEmployeesMax === undefined) {
          this.showMaxNumberOfEmployeesError = false;
          this.showNumberOfEmployeesError = false;
          return;
        }

        if (numberOfEmployeesMax < 0) {
          this.showMaxNumberOfEmployeesError = true;
          this.showNumberOfEmployeesError = false;
        } else {
          const numberOfEmployeesMin: number = this.marketplaceForm.get(
            "riskAppetite.numberOfEmployeesMin"
          )?.value;
          if (numberOfEmployeesMin === null || numberOfEmployeesMin === undefined) {
            this.showMaxNumberOfEmployeesError = false;
            this.showNumberOfEmployeesError = false;
            return;
          }
          if (numberOfEmployeesMin > numberOfEmployeesMax) {
            this.showMaxNumberOfEmployeesError = false;
            this.showNumberOfEmployeesError = true;
          } else {
            this.showMaxNumberOfEmployeesError = false;
            this.showNumberOfEmployeesError = false;
          }
        }
      });
  }

  private handleCommercialEstimatedPremiumValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Commercial Estimated Premium
    this.marketplaceForm
      .get("riskAppetite.commercialEstimatedPremiumMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((commercialEstimatedPremiumMin: number): void => {
        if (commercialEstimatedPremiumMin === null || commercialEstimatedPremiumMin === undefined) {
          this.showMinCommercialEstimatedPremiumError = false;
          this.showCommercialEstimatedPremiumError = false;
          return;
        }

        if (commercialEstimatedPremiumMin < 0) {
          this.showMinCommercialEstimatedPremiumError = true;
          this.showCommercialEstimatedPremiumError = false;
        } else {
          const commercialEstimatedPremiumMax: number = this.marketplaceForm.get(
            "riskAppetite.commercialEstimatedPremiumMax"
          )?.value;
          if (
            commercialEstimatedPremiumMax === null ||
            commercialEstimatedPremiumMax === undefined
          ) {
            this.showMinCommercialEstimatedPremiumError = false;
            this.showCommercialEstimatedPremiumError = false;
            return;
          }
          if (commercialEstimatedPremiumMin > commercialEstimatedPremiumMax) {
            this.showMinCommercialEstimatedPremiumError = false;
            this.showCommercialEstimatedPremiumError = true;
          } else {
            this.showMinCommercialEstimatedPremiumError = false;
            this.showCommercialEstimatedPremiumError = false;
          }
        }
      });

    // Max Commercial Estimated Premium
    this.marketplaceForm
      .get("riskAppetite.commercialEstimatedPremiumMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((commercialEstimatedPremiumMax: number): void => {
        if (commercialEstimatedPremiumMax === null || commercialEstimatedPremiumMax === undefined) {
          this.showMaxCommercialEstimatedPremiumError = false;
          this.showCommercialEstimatedPremiumError = false;
          return;
        }

        if (commercialEstimatedPremiumMax < 0) {
          this.showMaxCommercialEstimatedPremiumError = true;
          this.showCommercialEstimatedPremiumError = false;
        } else {
          const commercialEstimatedPremiumMin: number = this.marketplaceForm.get(
            "riskAppetite.commercialEstimatedPremiumMin"
          )?.value;
          if (
            commercialEstimatedPremiumMin === null ||
            commercialEstimatedPremiumMin === undefined
          ) {
            this.showMaxCommercialEstimatedPremiumError = false;
            this.showCommercialEstimatedPremiumError = false;
            return;
          }
          if (commercialEstimatedPremiumMin > commercialEstimatedPremiumMax) {
            this.showMaxCommercialEstimatedPremiumError = false;
            this.showCommercialEstimatedPremiumError = true;
          } else {
            this.showMaxCommercialEstimatedPremiumError = false;
            this.showCommercialEstimatedPremiumError = false;
          }
        }
      });
  }

  private handlePersonalAppetiteValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("riskAppetite.acceptPersonalLines")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((acceptPersonalLines: boolean): void => {
        if (acceptPersonalLines) {
          this.showBusinessLineRequiredError = false;
        } else {
          this.showBusinessLineError();
        }
      });
  }

  private handleNumberOfPersonalBusinessLinesValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Personal Business Lines
    this.marketplaceForm
      .get("riskAppetite.personalBusinessLinesMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((personalBusinessLinesMin: number): void => {
        if (personalBusinessLinesMin === null || personalBusinessLinesMin === undefined) {
          this.showMinPersonalBusinessLinesError = false;
          this.showPersonalBusinessLinesError = false;
          return;
        }

        if (personalBusinessLinesMin < 0) {
          this.showMinPersonalBusinessLinesError = true;
          this.showPersonalBusinessLinesError = false;
        } else {
          const personalBusinessLinesMax: number = this.marketplaceForm.get(
            "riskAppetite.personalBusinessLinesMax"
          )?.value;
          if (personalBusinessLinesMax === null || personalBusinessLinesMax === undefined) {
            this.showMinPersonalBusinessLinesError = false;
            this.showPersonalBusinessLinesError = false;
            return;
          }
          if (personalBusinessLinesMin > personalBusinessLinesMax) {
            this.showMinPersonalBusinessLinesError = false;
            this.showPersonalBusinessLinesError = true;
          } else {
            this.showMinPersonalBusinessLinesError = false;
            this.showPersonalBusinessLinesError = false;
          }
        }
      });

    // Max Personal Business Lines
    this.marketplaceForm
      .get("riskAppetite.personalBusinessLinesMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((personalBusinessLinesMax: number): void => {
        if (personalBusinessLinesMax === null || personalBusinessLinesMax === undefined) {
          this.showMaxPersonalBusinessLinesError = false;
          this.showPersonalBusinessLinesError = false;
          return;
        }

        if (personalBusinessLinesMax < 0) {
          this.showMaxPersonalBusinessLinesError = true;
          this.showPersonalBusinessLinesError = false;
        } else {
          const personalBusinessLinesMin: number = this.marketplaceForm.get(
            "riskAppetite.personalBusinessLinesMin"
          )?.value;
          if (personalBusinessLinesMin === null || personalBusinessLinesMin === undefined) {
            this.showMaxPersonalBusinessLinesError = false;
            this.showPersonalBusinessLinesError = false;
            return;
          }
          if (personalBusinessLinesMin > personalBusinessLinesMax) {
            this.showMaxPersonalBusinessLinesError = false;
            this.showPersonalBusinessLinesError = true;
          } else {
            this.showMaxPersonalBusinessLinesError = false;
            this.showPersonalBusinessLinesError = false;
          }
        }
      });
  }

  private handlePersonalEstimatedPremiumValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Personal Estimated Premium
    this.marketplaceForm
      .get("riskAppetite.personalEstimatedPremiumMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((personalEstimatedPremiumMin: number): void => {
        if (personalEstimatedPremiumMin === null || personalEstimatedPremiumMin === undefined) {
          this.showMinPersonalEstimatedPremiumError = false;
          this.showPersonalEstimatedPremiumError = false;
          return;
        }

        if (personalEstimatedPremiumMin < 0) {
          this.showMinPersonalEstimatedPremiumError = true;
          this.showPersonalEstimatedPremiumError = false;
        } else {
          const personalEstimatedPremiumMax: number = this.marketplaceForm.get(
            "riskAppetite.personalEstimatedPremiumMax"
          )?.value;
          if (personalEstimatedPremiumMax === null || personalEstimatedPremiumMax === undefined) {
            this.showMinPersonalEstimatedPremiumError = false;
            this.showPersonalEstimatedPremiumError = false;
            return;
          }
          if (personalEstimatedPremiumMin > personalEstimatedPremiumMax) {
            this.showMinPersonalEstimatedPremiumError = false;
            this.showPersonalEstimatedPremiumError = true;
          } else {
            this.showMinPersonalEstimatedPremiumError = false;
            this.showPersonalEstimatedPremiumError = false;
          }
        }
      });

    // Max Personal Estimated Premium
    this.marketplaceForm
      .get("riskAppetite.personalEstimatedPremiumMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((personalEstimatedPremiumMax: number): void => {
        if (personalEstimatedPremiumMax === null || personalEstimatedPremiumMax === undefined) {
          this.showMaxPersonalEstimatedPremiumError = false;
          this.showPersonalEstimatedPremiumError = false;
          return;
        }

        if (personalEstimatedPremiumMax < 0) {
          this.showMaxPersonalEstimatedPremiumError = true;
          this.showPersonalEstimatedPremiumError = false;
        } else {
          const personalEstimatedPremiumMin: number = this.marketplaceForm.get(
            "riskAppetite.personalEstimatedPremiumMin"
          )?.value;
          if (personalEstimatedPremiumMin === null || personalEstimatedPremiumMin === undefined) {
            this.showMaxPersonalEstimatedPremiumError = false;
            this.showPersonalEstimatedPremiumError = false;
            return;
          }
          if (personalEstimatedPremiumMin > personalEstimatedPremiumMax) {
            this.showMaxPersonalEstimatedPremiumError = false;
            this.showPersonalEstimatedPremiumError = true;
          } else {
            this.showMaxPersonalEstimatedPremiumError = false;
            this.showPersonalEstimatedPremiumError = false;
          }
        }
      });
  }

  private handleLifeAndAnnuitiesAppetiteValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("riskAppetite.acceptLifeAnnuitiesLines")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((acceptLifeAnnuitiesLines: boolean): void => {
        if (acceptLifeAnnuitiesLines) {
          this.showBusinessLineRequiredError = false;
        } else {
          this.showBusinessLineError();
        }
      });
  }

  private showBusinessLineError(): void {
    const riskAppetiteGroup: AbstractControl<any, any> | null = this.marketplaceForm.get(
      this.RISK_APPETITE_GROUP
    );
    const businessLineRequired: boolean = riskAppetiteGroup?.get("businessLineRequired")?.value;
    const commercialEnabled: boolean = riskAppetiteGroup?.get("acceptCommercialLines")?.value;
    const personalEnabled: boolean = riskAppetiteGroup?.get("acceptPersonalLines")?.value;
    const lifeAnnuitiesEnabled: boolean = riskAppetiteGroup?.get("acceptLifeAnnuitiesLines")?.value;

    if (businessLineRequired && !commercialEnabled && !personalEnabled && !lifeAnnuitiesEnabled) {
      this.showBusinessLineRequiredError = true;
    }
  }

  private handleNumberOfLifeAndAnnuitiesBusinessLinesValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Life and Annuities Business Lines
    this.marketplaceForm
      .get("riskAppetite.lifeAnnuitiesBusinessLinesMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((lifeAnnuitiesBusinessLinesMin: number): void => {
        if (lifeAnnuitiesBusinessLinesMin === null || lifeAnnuitiesBusinessLinesMin === undefined) {
          this.showMinLifeAnnuitiesBusinessLinesError = false;
          this.showLifeAnnuitiesBusinessLinesError = false;
          return;
        }

        if (lifeAnnuitiesBusinessLinesMin < 0) {
          this.showMinLifeAnnuitiesBusinessLinesError = true;
          this.showLifeAnnuitiesBusinessLinesError = false;
        } else {
          const lifeAnnuitiesBusinessLinesMax: number = this.marketplaceForm.get(
            "riskAppetite.lifeAnnuitiesBusinessLinesMax"
          )?.value;
          if (
            lifeAnnuitiesBusinessLinesMax === null ||
            lifeAnnuitiesBusinessLinesMax === undefined
          ) {
            this.showMinLifeAnnuitiesBusinessLinesError = false;
            this.showLifeAnnuitiesBusinessLinesError = false;
            return;
          }
          if (lifeAnnuitiesBusinessLinesMin > lifeAnnuitiesBusinessLinesMax) {
            this.showMinLifeAnnuitiesBusinessLinesError = false;
            this.showLifeAnnuitiesBusinessLinesError = true;
          } else {
            this.showMinLifeAnnuitiesBusinessLinesError = false;
            this.showLifeAnnuitiesBusinessLinesError = false;
          }
        }
      });

    // Max Life and Annuities Business Lines
    this.marketplaceForm
      .get("riskAppetite.lifeAnnuitiesBusinessLinesMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((lifeAnnuitiesBusinessLinesMax: number): void => {
        if (lifeAnnuitiesBusinessLinesMax === null || lifeAnnuitiesBusinessLinesMax === undefined) {
          this.showMaxLifeAnnuitiesBusinessLinesError = false;
          this.showLifeAnnuitiesBusinessLinesError = false;
          return;
        }

        if (lifeAnnuitiesBusinessLinesMax < 0) {
          this.showMaxLifeAnnuitiesBusinessLinesError = true;
          this.showLifeAnnuitiesBusinessLinesError = false;
        } else {
          const lifeAnnuitiesBusinessLinesMin: number = this.marketplaceForm.get(
            "riskAppetite.lifeAnnuitiesBusinessLinesMin"
          )?.value;
          if (
            lifeAnnuitiesBusinessLinesMin === null ||
            lifeAnnuitiesBusinessLinesMin === undefined
          ) {
            this.showMaxLifeAnnuitiesBusinessLinesError = false;
            this.showLifeAnnuitiesBusinessLinesError = false;
            return;
          }
          if (lifeAnnuitiesBusinessLinesMin > lifeAnnuitiesBusinessLinesMax) {
            this.showMaxLifeAnnuitiesBusinessLinesError = false;
            this.showLifeAnnuitiesBusinessLinesError = true;
          } else {
            this.showMaxLifeAnnuitiesBusinessLinesError = false;
            this.showLifeAnnuitiesBusinessLinesError = false;
          }
        }
      });
  }

  private handleDeathBenefitValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Min Death Benefit
    this.marketplaceForm
      .get("riskAppetite.deathBenefitMin")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((deathBenefitMin: number): void => {
        if (deathBenefitMin === null || deathBenefitMin === undefined) {
          this.showMinDeathBenefitError = false;
          this.showDeathBenefitError = false;
          return;
        }

        if (deathBenefitMin < 0) {
          this.showMinDeathBenefitError = true;
          this.showDeathBenefitError = false;
        } else {
          const deathBenefitMax: number = this.marketplaceForm.get(
            "riskAppetite.deathBenefitMax"
          )?.value;
          if (deathBenefitMax === null || deathBenefitMax === undefined) {
            this.showMinDeathBenefitError = false;
            this.showDeathBenefitError = false;
            return;
          }
          if (deathBenefitMin > deathBenefitMax) {
            this.showMinDeathBenefitError = false;
            this.showDeathBenefitError = true;
          } else {
            this.showMinDeathBenefitError = false;
            this.showDeathBenefitError = false;
          }
        }
      });

    // Max Death Benefit
    this.marketplaceForm
      .get("riskAppetite.deathBenefitMax")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((deathBenefitMax: number): void => {
        if (deathBenefitMax === null || deathBenefitMax === undefined) {
          this.showMaxDeathBenefitError = false;
          this.showDeathBenefitError = false;
          return;
        }

        if (deathBenefitMax < 0) {
          this.showMaxDeathBenefitError = true;
          this.showDeathBenefitError = false;
        } else {
          const deathBenefitMin: number = this.marketplaceForm.get(
            "riskAppetite.deathBenefitMin"
          )?.value;
          if (deathBenefitMin === null || deathBenefitMin === undefined) {
            this.showMaxDeathBenefitError = false;
            this.showDeathBenefitError = false;
            return;
          }
          if (deathBenefitMin > deathBenefitMax) {
            this.showMaxDeathBenefitError = false;
            this.showDeathBenefitError = true;
          } else {
            this.showMaxDeathBenefitError = false;
            this.showDeathBenefitError = false;
          }
        }
      });
  }

  private handleReferralDeliveryLimitsValueChanges(): void {
    if (!this.marketplaceForm) return;

    // Referrals per hour
    this.marketplaceForm
      .get("riskAppetite.maxReferralsPerHour")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((referralsPerHour: number): void => {
        if (referralsPerHour === null || referralsPerHour === undefined) {
          this.showReferralsPerHourError = false;
          return;
        }

        this.showReferralsPerHourError = referralsPerHour < 1;
      });

    // Referrals per day
    this.marketplaceForm
      .get("riskAppetite.maxReferralsPerDay")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((referralsPerDay: number): void => {
        if (referralsPerDay === null || referralsPerDay === undefined) {
          this.showReferralsPerDayError = false;
          return;
        }

        this.showReferralsPerDayError = referralsPerDay < 1;
      });

    // Referrals per week
    this.marketplaceForm
      .get("riskAppetite.maxReferralsPerWeek")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((referralsPerWeek: number): void => {
        if (referralsPerWeek === null || referralsPerWeek === undefined) {
          this.showReferralsPerWeekError = false;
          return;
        }

        this.showReferralsPerWeekError = referralsPerWeek < 1;
      });

    // Referrals per month
    this.marketplaceForm
      .get("riskAppetite.maxReferralsPerMonth")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((referralsPerMonth: number): void => {
        if (referralsPerMonth === null || referralsPerMonth === undefined) {
          this.showReferralsPerMonthError = false;
          return;
        }

        this.showReferralsPerMonthError = referralsPerMonth < 1;
      });
  }

  private handleMarketplaceReferralDeliveryDayError(): void {
    this.marketplaceForm
      .get(this.RISK_APPETITE_GROUP)
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((): void => {
        const riskAppetiteGroup = this.marketplaceForm.get(this.RISK_APPETITE_GROUP) as FormGroup;
        this.showDeliveryDaysError = riskAppetiteGroup?.hasError("noDeliveryDaySelected");
      });
  }

  private handleMarketplaceDeliveryDaysValueChanges(): void {
    if (!this.marketplaceForm) return;

    const monday$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.acceptReferralsOnMonday"
    )?.valueChanges;
    const tuesday$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.acceptReferralsOnTuesday"
    )?.valueChanges;
    const wednesday$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.acceptReferralsOnWednesday"
    )?.valueChanges;
    const thursday$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.acceptReferralsOnThursday"
    )?.valueChanges;
    const friday$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.acceptReferralsOnFriday"
    )?.valueChanges;
    const saturday$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.acceptReferralsOnSaturday"
    )?.valueChanges;
    const sunday$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.acceptReferralsOnSunday"
    )?.valueChanges;

    if (monday$ && tuesday$ && wednesday$ && thursday$ && friday$ && saturday$ && sunday$) {
      merge(monday$, tuesday$, wednesday$, thursday$, friday$, saturday$, sunday$)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((): void => {
          this.updateIndividualEmailRecipientSettings();
        });
    }
  }

  /**
   * Handles changes to marketplace-level delivery time settings (start time, end time, timezone)
   * and calls updateIndividualEmailRecipientSettings if any of them change.
   * Note: accept24hrReferralDelivery is handled by handleMarketplace24hrDeliveryValueChanges.
   */
  private handleMarketplaceDeliveryTimesValueChanges(): void {
    if (!this.marketplaceForm) return;

    const startTime$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.referralDeliveryStartTime"
    )?.valueChanges;
    const endTime$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.referralDeliveryEndTime"
    )?.valueChanges;
    const timezone$: Observable<any> | undefined = this.marketplaceForm.get(
      "riskAppetite.referralDeliveryTimezone"
    )?.valueChanges;

    if (startTime$ && endTime$ && timezone$) {
      merge(startTime$, endTime$, timezone$)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((): void => {
          this.updateIndividualEmailRecipientSettings();
        });
    }
  }

  private handleMarketplace24hrDeliveryValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("riskAppetite.accept24hrReferralDelivery")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((accept24hrReferralDelivery: boolean): void => {
        const riskAppetiteControl: AbstractControl<any, any> | null = this.marketplaceForm.get(
          this.RISK_APPETITE_GROUP
        );
        const startTimeControl: AbstractControl<any, any> | null = this.marketplaceForm.get(
          "riskAppetite.referralDeliveryStartTime"
        );
        const endTimeControl: AbstractControl<any, any> | null = this.marketplaceForm.get(
          "riskAppetite.referralDeliveryEndTime"
        );
        if (accept24hrReferralDelivery) {
          riskAppetiteControl?.patchValue({
            referralDeliveryStartTime: this.DEFAULT_DELIVERY_START_TIME,
            referralDeliveryEndTime: this.DEFAULT_DELIVERY_END_TIME
          });
          startTimeControl?.disable();
          endTimeControl?.disable();
          this.updateIndividualEmailRecipientSettings();
        } else {
          startTimeControl?.enable();
          endTimeControl?.enable();
          this.updateIndividualEmailRecipientSettings();
        }
      });
  }

  private handleApiDeliveryValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("deliverToApi")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((deliverToApi: boolean): void => {
        if (deliverToApi) {
          this.marketplaceForm.get("enableIndividualEmailDeliverySettings")?.setValue(false);
          this.marketplaceForm.get("enableIndividualEmailDeliverySettings")?.disable();
        } else {
          if (this.marketplace) {
            this.marketplaceForm
              .get("enableIndividualEmailDeliverySettings")
              ?.setValue(this.marketplace.enableIndividualEmailDeliverySettings);
            this.marketplaceForm.get("enableIndividualEmailDeliverySettings")?.enable();
          }
        }
      });
  }

  private handleEmailDeliveryValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("deliverToEmailAddress")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((deliverToEmailAddress: boolean): void => {
        if (deliverToEmailAddress) {
          const emailRecipients: string = this.marketplaceForm.get("emailRecipients")?.value;
          this.showEmailRecipientError = emailRecipients.trim() === "";
        } else {
          this.showDefaultPurchasingAgentEmailAddressError = false;
        }
      });
  }

  private handleEmailRecipientValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("emailRecipients")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((emailRecipients: string): void => {
        this.validateEmailRecipients(emailRecipients);
      });
  }

  private handleIndividualRecipientDeliverySettingsChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("enableIndividualEmailDeliverySettings")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((enableIndividualEmailDeliverySettings: boolean): void => {
        if (enableIndividualEmailDeliverySettings) {
        } else {
        }
      });
  }

  private handleMatchDeliveryDaysValueChanges(): void {
    if (!this.marketplaceForm) return;

    const individualEmailRecipientSettingGroup = this.marketplaceForm.get(
      this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP
    ) as FormGroup;

    Object.keys(individualEmailRecipientSettingGroup.controls).forEach(
      (controlName: string): void => {
        if (controlName.startsWith("sameDeliveryDaysAsMarketplace-")) {
          const sameDeliveryDaysAsMarketplaceControl: AbstractControl<any | any> | null =
            individualEmailRecipientSettingGroup.get(controlName);
          const recipientId: number = Number(
            controlName.split("sameDeliveryDaysAsMarketplace-")[1]
          );

          sameDeliveryDaysAsMarketplaceControl?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((sameDeliveryDaysAsMarketplace: boolean): void => {
              if (sameDeliveryDaysAsMarketplace) {
                this.setIndividualEmailRecipientDeliveryDaysInfoToMatchMarketplace(recipientId);
              } else {
                this.getRecipientAcceptReferralsOnMondayControl(recipientId)?.enable();
                this.getRecipientAcceptReferralsOnTuesdayControl(recipientId)?.enable();
                this.getRecipientAcceptReferralsOnWednesdayControl(recipientId)?.enable();
                this.getRecipientAcceptReferralsOnThursdayControl(recipientId)?.enable();
                this.getRecipientAcceptReferralsOnFridayControl(recipientId)?.enable();
                this.getRecipientAcceptReferralsOnSaturdayControl(recipientId)?.enable();
                this.getRecipientAcceptReferralsOnSundayControl(recipientId)?.enable();
              }
            });
        }
      }
    );
  }

  private getMarketplaceAcceptReferralsOnMondayControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.acceptReferralsOnMonday");
  }

  private getMarketplaceAcceptReferralsOnTuesdayControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.acceptReferralsOnTuesday");
  }

  private getMarketplaceAcceptReferralsOnWednesdayControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.acceptReferralsOnWednesday");
  }

  private getMarketplaceAcceptReferralsOnThursdayControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.acceptReferralsOnThursday");
  }

  private getMarketplaceAcceptReferralsOnFridayControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.acceptReferralsOnFriday");
  }

  private getMarketplaceAcceptReferralsOnSaturdayControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.acceptReferralsOnSaturday");
  }

  private getMarketplaceAcceptReferralsOnSundayControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.acceptReferralsOnSunday");
  }

  private handleMatchDeliveryTimesValueChanges(): void {
    if (!this.marketplaceForm) return;

    const individualEmailRecipientSettingGroup = this.marketplaceForm.get(
      this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP
    ) as FormGroup;

    Object.keys(individualEmailRecipientSettingGroup.controls).forEach(
      (controlName: string): void => {
        if (controlName.startsWith("sameDeliveryTimesAsMarketplace-")) {
          const sameDeliveryTimesAsMarketplaceControl: AbstractControl<any | any> | null =
            individualEmailRecipientSettingGroup.get(controlName);
          const recipientId: number = Number(
            controlName.split("sameDeliveryTimesAsMarketplace-")[1]
          );
          sameDeliveryTimesAsMarketplaceControl?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((sameDeliveryTimesAsMarketplace: boolean): void => {
              if (sameDeliveryTimesAsMarketplace) {
                this.setIndividualEmailRecipientDeliveryTimesInfoToMatchMarketplace(recipientId);
              } else {
                this.getRecipientReferralDeliveryStartTimeControl(recipientId)?.enable();
                this.getRecipientReferralDeliveryEndTimeControl(recipientId)?.enable();
                this.getRecipientReferralDeliveryTimezoneControl(recipientId)?.enable();
                this.getRecipientAccept24hrReferralDeliveryControl(recipientId)?.enable();
              }
            });
        }
      }
    );
  }

  private setIndividualEmailRecipientDeliveryTimesInfoToMatchMarketplace(
    recipientId: number
  ): void {
    const startTimeControl: AbstractControl<any, any> | null =
      this.getRecipientReferralDeliveryStartTimeControl(recipientId);
    const endTimeControl: AbstractControl<any, any> | null =
      this.getRecipientReferralDeliveryEndTimeControl(recipientId);
    const timeZoneControl: AbstractControl<any, any> | null =
      this.getRecipientReferralDeliveryTimezoneControl(recipientId);
    const accept24hrControl: AbstractControl<any, any> | null =
      this.getRecipientAccept24hrReferralDeliveryControl(recipientId);

    // Set the recipient delivery times information to match that of the marketplace
    startTimeControl?.setValue(this.getMarketplaceReferralDeliveryStartTimeControl()?.value);
    endTimeControl?.setValue(this.getMarketplaceReferralDeliveryEndTimeControl()?.value);
    timeZoneControl?.setValue(this.marketplaceReferralDeliveryTimezoneControl()?.value);
    accept24hrControl?.setValue(this.getMarketplace24hrReferralDeliveryControl()?.value);

    startTimeControl?.disable();
    endTimeControl?.disable();
    timeZoneControl?.disable();
    accept24hrControl?.disable();
  }

  private getRecipientReferralDeliveryStartTimeControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.referralDeliveryStartTime-${recipientId}`
    );
  }

  private getRecipientReferralDeliveryEndTimeControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.referralDeliveryEndTime-${recipientId}`
    );
  }

  private getRecipientReferralDeliveryTimezoneControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.referralDeliveryTimezone-${recipientId}`
    );
  }

  private getRecipientAccept24hrReferralDeliveryControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.accept24hrReferralDelivery-${recipientId}`
    );
  }

  private getMarketplaceReferralDeliveryStartTimeControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.referralDeliveryStartTime");
  }

  private getMarketplaceReferralDeliveryEndTimeControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.referralDeliveryEndTime");
  }

  private marketplaceReferralDeliveryTimezoneControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.referralDeliveryTimezone");
  }

  private getMarketplace24hrReferralDeliveryControl(): AbstractControl<any, any> | null {
    return this.marketplaceForm.get("riskAppetite.accept24hrReferralDelivery");
  }

  private setIndividualEmailRecipientDeliveryDaysInfoToMatchMarketplace(recipientId: number): void {
    const mondayControl: AbstractControl<any, any> | null =
      this.getRecipientAcceptReferralsOnMondayControl(recipientId);
    const tuesdayControl: AbstractControl<any, any> | null =
      this.getRecipientAcceptReferralsOnTuesdayControl(recipientId);
    const wednesdayControl: AbstractControl<any, any> | null =
      this.getRecipientAcceptReferralsOnWednesdayControl(recipientId);
    const thursdayControl: AbstractControl<any, any> | null =
      this.getRecipientAcceptReferralsOnThursdayControl(recipientId);
    const fridayControl: AbstractControl<any, any> | null =
      this.getRecipientAcceptReferralsOnFridayControl(recipientId);
    const saturdayControl: AbstractControl<any, any> | null =
      this.getRecipientAcceptReferralsOnSaturdayControl(recipientId);
    const sundayControl: AbstractControl<any, any> | null =
      this.getRecipientAcceptReferralsOnSundayControl(recipientId);

    // Set the recipient delivery days information to match that of the marketplace
    mondayControl?.setValue(this.getMarketplaceAcceptReferralsOnMondayControl()?.value);
    tuesdayControl?.setValue(this.getMarketplaceAcceptReferralsOnTuesdayControl()?.value);
    wednesdayControl?.setValue(this.getMarketplaceAcceptReferralsOnWednesdayControl()?.value);
    thursdayControl?.setValue(this.getMarketplaceAcceptReferralsOnThursdayControl()?.value);
    fridayControl?.setValue(this.getMarketplaceAcceptReferralsOnFridayControl()?.value);
    saturdayControl?.setValue(this.getMarketplaceAcceptReferralsOnSaturdayControl()?.value);
    sundayControl?.setValue(this.getMarketplaceAcceptReferralsOnSundayControl()?.value);

    mondayControl?.disable();
    tuesdayControl?.disable();
    wednesdayControl?.disable();
    thursdayControl?.disable();
    fridayControl?.disable();
    saturdayControl?.disable();
    sundayControl?.disable();
  }

  private getRecipientAcceptReferralsOnMondayControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.acceptReferralsOnMonday-${recipientId}`
    );
  }

  private getRecipientAcceptReferralsOnTuesdayControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.acceptReferralsOnTuesday-${recipientId}`
    );
  }

  private getRecipientAcceptReferralsOnWednesdayControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.acceptReferralsOnWednesday-${recipientId}`
    );
  }

  private getRecipientAcceptReferralsOnThursdayControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.acceptReferralsOnThursday-${recipientId}`
    );
  }

  private getRecipientAcceptReferralsOnFridayControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.acceptReferralsOnFriday-${recipientId}`
    );
  }

  private getRecipientAcceptReferralsOnSaturdayControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.acceptReferralsOnSaturday-${recipientId}`
    );
  }

  private getRecipientAcceptReferralsOnSundayControl(
    recipientId: number
  ): AbstractControl<any, any> | null {
    return this.marketplaceForm.get(
      `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.acceptReferralsOnSunday-${recipientId}`
    );
  }

  private handleIndividualRecipient24hrDeliveryValueChanges(): void {
    if (!this.marketplaceForm) return;

    const individualEmailRecipientSettingGroup = this.marketplaceForm.get(
      this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP
    ) as FormGroup;

    Object.keys(individualEmailRecipientSettingGroup.controls).forEach(
      (controlName: string): void => {
        if (controlName.startsWith("accept24hrReferralDelivery-")) {
          const accept24hrReferralDeliveryControl: AbstractControl<any | any> | null =
            individualEmailRecipientSettingGroup.get(controlName);
          const recipientId: number = Number(controlName.split("accept24hrReferralDelivery-")[1]);

          accept24hrReferralDeliveryControl?.valueChanges
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((accept24hrReferralDelivery: boolean): void => {
              const startTimeControl: AbstractControl<any, any> | null =
                this.getRecipientReferralDeliveryStartTimeControl(recipientId);
              const endTimeControl: AbstractControl<any, any> | null =
                this.getRecipientReferralDeliveryEndTimeControl(recipientId);

              if (accept24hrReferralDelivery) {
                startTimeControl?.setValue(this.DEFAULT_DELIVERY_START_TIME);
                endTimeControl?.setValue(this.DEFAULT_DELIVERY_END_TIME);
                startTimeControl?.disable();
                endTimeControl?.disable();
              } else if (
                !this.marketplaceForm.get(
                  `${this.INDIVIDUAL_EMAIL_RECIPIENT_SETTINGS_GROUP}.sameDeliveryTimesAsMarketplace-${recipientId}`
                )?.value
              ) {
                startTimeControl?.enable();
                endTimeControl?.enable();
              }
            });
        }
      }
    );
  }

  private handleEmailBccRecipientValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("emailBccRecipients")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((emailBccRecipients: string): void => {
        this.validateEmailBccRecipients(emailBccRecipients);
      });
  }

  private validateEmailRecipients(emailRecipients: string | null | undefined): void {
    if (!emailRecipients || emailRecipients.trim() === "") {
      this.emailRecipientsIsInvalid = false;
      this.showEmailRecipientError = true;
      this.showDuplicateEmailRecipientError = false;
      this.marketplaceForm.get("enableIndividualEmailDeliverySettings")?.setValue(false);
      return;
    }

    const commaSeperatedEmailAddressPattern =
      /^([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]{2,}\.[A-Za-z]{2,})(,\s*[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]{2,}\.[A-Za-z]{2,})*$/;
    this.emailRecipientsIsInvalid = !commaSeperatedEmailAddressPattern.test(emailRecipients.trim());
    this.showEmailRecipientError = false;

    const recipients: string[] = emailRecipients
      .split(",")
      .map((emailAddress: string): string => emailAddress.trim());

    this.showDuplicateEmailRecipientError = this.hasDuplicateEmailRecipient(recipients);
  }

  private hasDuplicateEmailRecipient(emailRecipients: string[]): boolean {
    const lowerCaseEmailAddress: string[] = emailRecipients.map((emailRecipient: string): string =>
      emailRecipient.toLowerCase().trim()
    );
    return new Set(lowerCaseEmailAddress).size !== emailRecipients.length;
  }

  private validateEmailBccRecipients(emailBccRecipients: string | null | undefined): void {
    if (!emailBccRecipients || emailBccRecipients.trim() === "") {
      this.emailBccRecipientsIsInvalid = false;
      this.showDuplicateEmailBccRecipientError = false;
      return;
    }

    const commaSeperatedEmailAddressPattern =
      /^([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]{2,}\.[A-Za-z]{2,})(,\s*[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]{2,}\.[A-Za-z]{2,})*$/;
    this.emailBccRecipientsIsInvalid = !commaSeperatedEmailAddressPattern.test(
      emailBccRecipients.trim()
    );

    const recipients: string[] = emailBccRecipients
      .split(",")
      .map((emailAddress: string): string => emailAddress.trim());

    this.showDuplicateEmailBccRecipientError = this.hasDuplicateEmailRecipient(recipients);
  }

  private handleNaicsModeValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get(this.NAICS_MODE)
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((naicsMode: string): void => {
        if (naicsMode.toLowerCase() === this.EXCLUDE_NAICS_MODE) {
          this.setExcludeNaicsCodes();
        } else if (naicsMode.toLowerCase() === this.INCLUDE_NAICS_MODE) {
          this.setIncludedNaicsCodes();
        }
      });
  }

  private setExcludeNaicsCodes(): void {
    this.marketplaceForm
      .get(this.RISK_APPETITE_GROUP)
      ?.patchValue({ naicsCodes: this.excludedNaicsCodes?.join(", ") });
  }

  private setIncludedNaicsCodes(): void {
    const includedNaicsCodes: string[] = this.allNaicsCodes
      .filter((naicsCode: string | null): naicsCode is string => naicsCode !== null)
      .filter((naicsCode: string): boolean => !this.excludedNaicsCodes?.includes(naicsCode));
    this.marketplaceForm.get(this.RISK_APPETITE_GROUP)?.patchValue({
      naicsCodes: includedNaicsCodes.join(", ")
    });
  }

  protected getReferralDisplayLabel(method: ReferralDeliveryMethodEnum): string {
    return `${method} API`;
  }

  getEmailTemplateDisplayLabel(template: EmailTemplateEnum): string {
    switch (template) {
      case EmailTemplateEnum.VIU_COMMERCIAL:
        return "VIU Commercial";
      case EmailTemplateEnum.DEFAULT:
      default:
        return "Default";
    }
  }

  selectAllStates(): void {
    const statesGroup = this.marketplaceForm.get(this.STATE_GROUP) as FormGroup;
    Object.keys(statesGroup.controls).forEach((stateCode: string) => {
      statesGroup.get(stateCode)?.setValue(true);
    });
    statesGroup.markAsDirty();
  }
  clearAllStates(): void {
    const statesGroup = this.marketplaceForm.get(this.STATE_GROUP) as FormGroup;
    Object.keys(statesGroup.controls).forEach((stateCode: string) => {
      statesGroup.get(stateCode)?.setValue(false);
    });
  }
  selectAllReferralSources(): void {
    const referralSourceGroup = this.marketplaceForm.get(this.REFERRAL_SOURCES_GROUP) as FormGroup;
    Object.keys(referralSourceGroup.controls).forEach((referralSource: string) => {
      const control = referralSourceGroup.get([referralSource]);
      // Only set value if the control is not disabled
      if (control && !control.disabled) {
        control.setValue(true);
      }
    });
    referralSourceGroup.markAsDirty();
  }
  clearAllReferralSources(): void {
    const referralSourceGroup = this.marketplaceForm.get(this.REFERRAL_SOURCES_GROUP) as FormGroup;
    Object.keys(referralSourceGroup.controls).forEach((referralSource: string) => {
      referralSourceGroup.get([referralSource])?.setValue(false);
    });
  }

  private handlePremierPartnerMarketplaceValueChanges(): void {
    if (!this.marketplaceForm) return;

    this.marketplaceForm
      .get("premierPartnerMarketplace")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((isPremierPartner: boolean): void => {
        const paymentOptionControl = this.marketplaceForm.get("paymentOption");
        const currentPaymentOption = paymentOptionControl?.value;

        if (!isPremierPartner) {
          // If not a premier partner and currently set to debit prepaid balance,
          // reset to billed
          if (currentPaymentOption === this.paymentOptionEnum.DEBIT_PREPAID_BALANCE) {
            paymentOptionControl?.setValue(this.paymentOptionEnum.BILLED);
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.stateManagementService.resetHeadingAndTitle();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
